/**
 * MOCK HANDLERS — MSW handler per le entita' del core.
 *
 * Ogni handler intercetta una rotta del backend e risponde con dati
 * dal modulo `mocks/`. I plugin contribuiscono i propri handler tramite
 * PluginManifest.getMockHandlers(), aggregati in mockWorker.ts.
 */

import { http, HttpResponse, delay } from 'msw';
import { mockQueues } from './mocks/queueMocks';
import { zoneHandlers } from './mocks/zoneMocks';
import { mapPointHandlers } from './mocks/mapPointMocks';
import { mockTaskVisible } from './mocks/taskVisibilityMocks';
import { inMockDateRange } from './mocks/dateRangeMocks';
import {
  mockAutomations,
  mockAutomationCategories,
  mockAutomationActionTypes,
  mockAutomationObjectTypes,
  mockConditionTypesByClassName,
  mockUpdatableFieldsByClassName,
  mockAutomationStats,
  mockAutomationRuns,
  validateAutomationDraft,
  UPDATE_FIELD_ACTION_CODE,
} from './mocks/automationMocks';
import { automationToDto, automationRunToDto } from '../entities/automation/automationMappers';
import type { Automation, AutomationCreatePayload } from '../entities/automation/automationTypes';
import { mockTasks } from './mocks/taskMocks';
import { mockNotes } from './mocks/noteMocks';
import { mockLinkedinPosts } from './mocks/linkedinPostMocks';
import { mockCalls } from './mocks/callMocks';
import { mockEmails } from './mocks/emailMocks';
import { mockMeetings } from './mocks/meetingMocks';
import { mockBusinesses } from './mocks/businessMocks';
import { mockPersons } from './mocks/personMocks';
import { mockPersonSources, consumeNextPersonSourceId } from './mocks/personSourceMocks';
import { mockPersonChannels, consumeNextPersonChannelId } from './mocks/personChannelMocks';
import { mockRapidContacts } from './mocks/rapidContactMocks';
import { mockRelationships } from './mocks/relationshipMocks';
import { mockMessages } from './mocks/messageMocks';
import {
  mockMessageInfos,
  mockPredefinedMessageInfos,
  groupMockInfosByExtractor,
} from './mocks/messageInfoMocks';
import { mockQuotations, mockQuotationFiles, mockQuotationItems, withMockPreview } from './mocks/quotationMocks';
import {
  quotationToDto,
  quotationInputFromDto,
  isValidQuotationStatus,
  type QuotationInput,
} from '../entities/quotation/quotationMappers';
import type { Quotation } from '../entities/quotation/quotationTypes';
import { mockPipelines } from './mocks/pipelineMocks';
import {
  pipelineToDto,
  KIND_FROM_INT,
  type PipelineInput,
  type PipelineStateInput,
} from '../entities/pipeline/pipelineMappers';
import type { Pipeline, PipelineState } from '../entities/pipeline/pipelineTypes';
import { kanbanStateIds, mockKanbans } from './mocks/kanbanMocks';
import {
  mockFilterFields,
  matchesBusinessKanbanFilter,
  matchesKanbanFilter,
  matchesPersonKanbanFilter,
  matchesTaskKanbanFilter,
  validateKanbanFilter,
} from './mocks/kanbanFilterMocks';
import {
  kanbanToDto,
  kanbanInputFromDto,
  type KanbanInput,
} from '../entities/kanban/kanbanMappers';
import type { Kanban } from '../entities/kanban/kanbanTypes';
import {
  quotationFileToDto,
  quotationFileTypeFromDto,
} from '../entities/quotationFile/quotationFileMappers';
import type { QuotationFile } from '../entities/quotationFile/quotationFileTypes';
import type { QuotationItem } from '../entities/quotationItem/quotationItemMappers';
import { mockCountries, mockRegionsByCountry } from './mocks/geographyMocks';
import { mockComments } from './mocks/commentMocks';
import { mockTeams, consumeNextTeamId, type MockTeam } from './mocks/teamMocks';
import { mockAuditLog } from './mocks/auditLogMocks';
import type { MemberDto } from '../entities/user/userApi';
import type { User } from '../auth/types';
import { mockWhatsAppMessages } from './mocks/whatsAppMessageMocks';
import { mockWhatsAppSessions } from './mocks/whatsAppSessionMocks';
import { WHATSAPP_SESSION_FQN } from '../components/useEntityActivities';
import { whatsAppMessageToDto } from '../entities/whatsAppMessage/whatsAppMessageMappers';
import {
  phoneDigits,
  type WhatsAppMessageType,
} from '../entities/whatsAppMessage/whatsAppMessageTypes';
import {
  personToDto,
  personInputFromDto,
  type PersonInput,
  type Person,
} from '../entities/person/personMappers';
import {
  rapidContactToDto,
  rapidContactInputFromDto,
  type RapidContactInput,
  type RapidContact,
} from '../entities/rapidContact/rapidContactMappers';
import {
  businessToDto,
  businessInputFromDto,
  type BusinessInput,
  type Business,
} from '../entities/business/businessMappers';
import {
  taskToDto,
  taskInputFromDto,
  taskStatusInputFromDto,
  taskStatusToDto,
  type TaskInput,
  type TaskStatusInput,
} from '../entities/task/taskMappers';
import type { Task } from '../entities/task/taskTypes';
import type { ActivitySummary } from '../entities/activity/activityTypes';
import {
  noteToDto,
  noteInputFromDto,
  type NoteInput,
} from '../entities/note/noteMappers';
import {
  linkedinPostToDto,
  linkedinPostInputFromDto,
  type LinkedinPostInput,
} from '../entities/linkedinPost/linkedinPostMappers';
import type { LinkedinPost } from '../entities/linkedinPost/linkedinPostTypes';
import type { Note } from '../entities/note/noteTypes';
import {
  callToDto,
  callInputFromDto,
  type CallInput,
} from '../entities/call/callMappers';
import type { Call, CallDirection } from '../entities/call/callTypes';
import {
  emailToDto,
  emailInputFromDto,
  type EmailInput,
} from '../entities/email/emailMappers';
import type { Email, EmailDirection } from '../entities/email/emailTypes';
import {
  meetingToDto,
  meetingInputFromDto,
  type MeetingInput,
} from '../entities/meeting/meetingMappers';
import type { Meeting } from '../entities/meeting/meetingTypes';
import type { Comment, CommentCreatePayload } from '../entities/comment/commentTypes';
import {
  messageToDto,
  messageInputFromDto,
  messageLabelToInt,
  type MessageInput,
} from '../entities/message/messageMappers';
import { MESSAGE_LABELS, type Message } from '../entities/message/messageTypes';
import {
  messageInfoToDto,
  messageInfoInputFromDto,
  type MessageInfoInput,
} from '../entities/message/messageInfoMappers';
import type { MessageInfo } from '../entities/message/messageInfoTypes';
import { listTypeHandlers } from './mocks/listTypeMocks';
import { MOCK_GROUP_ID } from './mocks/mockSite';

let nextId = 10000;

/**
 * Blob dei media inviati a runtime dal mock (whatsAppMessageId -> Blob), serviti da
 * GET /whatsapp-message/{id}/media/content. I dati seed NON contengono media: il
 * contenuto esiste solo per i media inviati durante la sessione mock.
 */
const mockMediaBlobs = new Map<number, Blob>();

/**
 * SYNC con `DomainUtil.normalize` (Java, crm-util). Forma canonica di un dominio: minuscolo,
 * senza schema, senza `www.`, senza credenziali/porta/path, senza punto finale.
 *
 * Qui la duplicazione e' inevitabile e voluta — il mock E' un finto backend, e deve rispondere
 * come quello vero. Un mock piu' permissivo (o piu' severo) del backend nasconde il difetto
 * invece di rivelarlo. Se cambia la regola in Java, cambiala anche qui.
 */
function normalizeMockDomain(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let result = raw.trim().toLowerCase();
  const schemeEnd = result.indexOf('://');
  if (schemeEnd >= 0) result = result.slice(schemeEnd + 3);
  for (const separator of ['/', '?', '#']) {
    const index = result.indexOf(separator);
    if (index >= 0) result = result.slice(0, index);
  }
  const at = result.lastIndexOf('@');
  if (at >= 0) result = result.slice(at + 1);
  const colon = result.indexOf(':');
  if (colon >= 0) result = result.slice(0, colon);
  if (result.startsWith('www.')) result = result.slice(4);
  result = result.replace(/\.+$/, '').trim();
  return result || null;
}

/** SYNC con `DomainUtil.fromEmailAddress` (Java, crm-util). */
function mockDomainFromEmail(emailAddress: string | null | undefined): string | null {
  if (!emailAddress) return null;
  const trimmed = emailAddress.trim();
  const at = trimmed.lastIndexOf('@');
  if (at < 0 || at === trimmed.length - 1) return null;
  return normalizeMockDomain(trimmed.slice(at + 1));
}

/** SYNC con `BusinessLocalServiceImpl._normalizeName` (Java). */
function normalizeMockName(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const result = raw.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
  return result || null;
}

/**
 * SYNC con `AddressRules.isPartial` (Java, FILO-52): un indirizzo compilato in parte, anche col solo nome,
 * prende «-» al posto di via e città; uno del tutto vuoto lo rifiuta Liferay.
 */
function isPartialAddress(a: {
  name?: string; description?: string; street?: string; city?: string; zip?: string; regionId?: number | null;
}): boolean {
  return [a.name, a.description, a.street, a.city, a.zip].some((v) => v?.trim()) || (a.regionId ?? 0) > 0;
}

/**
 * SYNC con `BusinessLocalServiceImpl.resolve` (Java): tre candidati in cascata, dal piu' forte
 * al piu' debole. Si passa al successivo quando il precedente NON TROVA, non quando e' assente.
 */
function resolveMockBusiness(
  name: string | null,
  domain: string | null,
  emailAddress: string | null,
) {
  // Solo il CANDIDATO viene canonicalizzato, non il valore salvato: il backend confronta col
  // finder unique G_D contro `Business.domain` cosi' com'e' stato registrato (addBusiness fa
  // solo trim). Normalizzare anche questo lato renderebbe il mock piu' capace del backend —
  // troverebbe un'azienda salvata come "www.acme.com" che in produzione resta invisibile — e
  // il difetto si scoprirebbe solo dal vivo. Il lowercase invece c'e' perche' la collation
  // MySQL e' case-insensitive.
  const byDomain = (candidate: string | null) =>
    candidate
      ? mockBusinesses.find((b) => b.domain?.trim().toLowerCase() === candidate)
      : undefined;

  const found = byDomain(normalizeMockDomain(domain)) ?? byDomain(mockDomainFromEmail(emailAddress));
  if (found) return found;

  const target = normalizeMockName(name);
  if (!target) return undefined;
  return mockBusinesses.find((b) => normalizeMockName(b.name) === target);
}

/** Match numero per suffisso, come PhoneNormalizer.suffixMatches lato backend. */
function waSuffixMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  const shorter = Math.min(a.length, b.length);
  if (shorter < 7) return false;
  return a.length >= b.length ? a.endsWith(b) : b.endsWith(a);
}

/**
 * attach-or-create della sessione WhatsApp per un numero (regola 24h), condiviso
 * dagli handler di invio testo e media (replica WhatsAppSessionLocalService.attachOrCreate).
 */
function attachOrCreateMockSession(
  digits: string,
  personId: number,
  nowMs: number,
  nowIso: string,
): (typeof mockWhatsAppSessions)[number] {
  const DAY = 24 * 3600 * 1000;
  let session: (typeof mockWhatsAppSessions)[number] | null = null;
  let bestLast = 0;
  for (const candidate of mockWhatsAppSessions.filter((s) =>
    waSuffixMatch(digits, String(s.phoneNumber ?? '')),
  )) {
    const last = mockWhatsAppMessages
      .filter((m) => m.whatsAppSessionId === candidate.whatsAppSessionId)
      .reduce((acc, m) => Math.max(acc, new Date(m.receiptDate).getTime()), 0);
    if (last > bestLast) {
      bestLast = last;
      session = candidate;
    }
  }
  if (!session || nowMs - bestLast > DAY) {
    session = {
      whatsAppSessionId: nextId++,
      phoneNumber: digits,
      phoneNumberId: '1083835191482152',
      personId,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: nowIso,
      modifiedDate: nowIso,
    };
    mockWhatsAppSessions.push(session);
  }
  return session;
}

/** I messaggi di una sessione in ordine cronologico, come li restituisce il backend. */
function mockSessionMessages(whatsAppSessionId: number) {
  return mockWhatsAppMessages
    .filter((m) => m.whatsAppSessionId === whatsAppSessionId)
    .sort((a, b) => (a.receiptDate ?? '').localeCompare(b.receiptDate ?? ''));
}

/** `lastMessageDate` di una sessione: l'ultimo messaggio, o la creazione se non ne ha. */
function mockSessionLastMessageDate(session: { whatsAppSessionId: number; createDate: string }) {
  const msgs = mockSessionMessages(session.whatsAppSessionId);
  return msgs.length ? msgs[msgs.length - 1].receiptDate : session.createDate;
}

/**
 * Stato della finestra di 24h di Meta su una sessione (replica WhatsAppWindow).
 *
 * Guarda SOLO i messaggi in ingresso: un nostro invio non apre e non prolunga la
 * finestra. È una regola diversa da quella che raggruppa i messaggi in accordion
 * (attachOrCreateMockSession, che guarda l'ultimo messaggio qualunque esso sia), e
 * tenerle distinte è tutto il punto: prima erano la stessa cosa, e un nostro OUT
 * faceva risultare "attiva" una conversazione che Meta considerava chiusa.
 */
function mockWhatsAppWindow(whatsAppSessionId: number): {
  windowState: 'OPEN' | 'PENDING' | 'CLOSED';
  windowExpirationDate?: string;
} {
  const DAY = 24 * 3600 * 1000;

  const lastInbound = mockWhatsAppMessages
    .filter(
      (m) => m.whatsAppSessionId === whatsAppSessionId && m.direction === 'IN',
    )
    .reduce((acc, m) => Math.max(acc, new Date(m.receiptDate).getTime()), 0);

  if (!lastInbound) return { windowState: 'PENDING' };

  const expiration = lastInbound + DAY;

  if (Date.now() >= expiration) return { windowState: 'CLOSED' };

  return {
    windowState: 'OPEN',
    windowExpirationDate: new Date(expiration).toISOString(),
  };
}

/**
 * La sessione CORRENTE di un numero: quella col messaggio più recente.
 *
 * È la stessa regola di `attachOrCreateMockSession`, e sta in un posto solo perché prima
 * non era così: `mockFreeFormAllowed` e `mockWindowConflict` prendevano `.at(-1)`, cioè
 * l'ultima nell'ORDINE DELL'ARRAY. Un contatto con più sessioni sullo stesso numero (es.
 * Person 19: 71004 aperta, 71005 scaduta, 71006 in attesa) finiva così sulla sessione
 * sbagliata, e il composer di una sessione aperta prendeva comunque 409 — il mock rendeva
 * improvabile proprio il caso nominale che i mock esistono per provare.
 */
function findMockSessionFor(digits: string, personId: number) {
  let session: (typeof mockWhatsAppSessions)[number] | undefined;
  let bestLast = -1;

  for (const candidate of mockWhatsAppSessions) {
    if (!waSuffixMatch(digits, String(candidate.phoneNumber ?? ''))) continue;
    if (personId > 0 && candidate.personId !== personId) continue;

    const last = mockWhatsAppMessages
      .filter((m) => m.whatsAppSessionId === candidate.whatsAppSessionId)
      .reduce((acc, m) => Math.max(acc, new Date(m.receiptDate).getTime()), 0);

    if (last > bestLast) {
      bestLast = last;
      session = candidate;
    }
  }

  return session;
}

/** Predicato unico del gate, come isFreeFormAllowed lato server. */
function mockFreeFormAllowed(digits: string, personId: number): boolean {
  const session = findMockSessionFor(digits, personId);

  if (!session) return false;

  const window = mockWhatsAppWindow(session.whatsAppSessionId);

  return window.windowState === 'OPEN';
}

/** Corpo del 409 quando si tenta di scrivere a finestra chiusa. */
function mockWindowConflict(digits: string, personId: number) {
  const session = findMockSessionFor(digits, personId);

  const window = session
    ? mockWhatsAppWindow(session.whatsAppSessionId)
    : { windowState: 'PENDING' as const, windowExpirationDate: undefined };

  return HttpResponse.json(
    {
      windowState: window.windowState,
      windowExpirationDate: window.windowExpirationDate,
      message:
        'La finestra di 24 ore verso ' +
        digits +
        ' è chiusa: Meta accetta solo template approvati.',
      // Template di ricontatto configurato: la UI lo preseleziona e chiede conferma.
      template: mockWhatsAppTemplates[0],
    },
    { status: 409 },
  );
}

/**
 * Template approvati su Meta. Il primo è anche quello configurato come template di
 * ricontatto (vedi mockWindowConflict): l'invio a sessione scaduta lo propone
 * preselezionato, mostrando il testo che il contatto riceverà davvero.
 */
const mockWhatsAppTemplates = [
  {
    name: 'riapertura_conversazione',
    language: 'it',
    category: 'UTILITY',
    body: 'Buongiorno, la ricontattiamo dal nostro CRM.\n{{1}}',
    parameterCount: 1,
  },
  {
    name: 'promemoria_appuntamento',
    language: 'it',
    category: 'UTILITY',
    body: 'Le ricordiamo l\'appuntamento del {{1}} alle {{2}}.',
    parameterCount: 2,
  },
];

/**
 * Numeri WhatsApp mittente della company (TS-89). Due numeri, così la combo di
 * scelta del mittente compare nel modale di invio (con uno solo resterebbe
 * nascosta). Il primo coincide col numero business dei messaggi seed.
 */
const mockWhatsAppNumbers = [
  {
    whatsAppNumberId: 81001,
    phoneNumberId: '1083835191482152',
    phoneNumber: '+39 351 0000001',
    label: 'Assistenza',
  },
  {
    whatsAppNumberId: 81002,
    phoneNumberId: '1083835191482153',
    phoneNumber: '+39 351 0000002',
    label: 'Commerciale',
  },
];

/**
 * Join mock-side tra Message e MessageInfo: replica l'eager-load del
 * backend (DTOConverterUtil.toMessage), che splitta le info in due campi:
 *   - `infos`          <- info predefinite (mockPredefinedMessageInfos), read-only,
 *                         ordinate per priority asc
 *   - `extractorInfos` <- info degli extractor (mockMessageInfos), editabili,
 *                         raggruppate per extractor di provenienza (ExtractorGroup[])
 *                         e ordinate per priority dell'extractor
 * Non muta l'input.
 */
function enrichMessageInfos(m: Message): Message {
  return {
    ...m,
    infos: mockPredefinedMessageInfos
      .filter((mi) => mi.messageId === m.messageId)
      .sort((a, b) => a.priority - b.priority),
    extractorInfos: groupMockInfosByExtractor(
      mockMessageInfos.filter((mi) => mi.messageId === m.messageId),
    ),
  };
}

/**
 * Prefisso base per tutte le rotte mockate. Usiamo un pathname relativo
 * senza origine: MSW matcha sulla base del pathname indipendentemente
 * dall'host/porta (funziona con localhost:3000, 3001, o qualsiasi altro).
 *
 * Allineato a `httpClient.defaults.baseURL` (VITE_CRM_BASE_URL, default
 * '/o/crm/v1_0'). Se il baseURL cambia, aggiornare anche questo valore
 * — altrimenti MSW non matcha e le richieste fanno passthrough al
 * Vite proxy -> backend reale.
 */
const API = '/o/crm/v1_0';

// Le regole predefinite dei task di follow-up (FILO-67), le stesse di RapidContactTaskRule.DEFAULTS.
const MOCK_DEFAULT_TASK_RULES = [
  { level: 'ZERO', task: false, priority: 2, days: 15, type: 2 },
  { level: 'ONE', task: false, priority: 2, days: 15, type: 2 },
  { level: 'TWO', task: false, priority: 2, days: 15, type: 2 },
  { level: 'THREE', task: true, priority: 3, days: 5, type: 2 },
  { level: 'FOUR', task: true, priority: 4, days: 2, type: 2 },
  { level: 'FIVE', task: true, priority: 4, days: 0, type: 2 },
];
let mockTaskRules: typeof MOCK_DEFAULT_TASK_RULES | null = null;

// TS-59: FQN dei tipi attività, allineati ai model backend (it.dvel.crm.core.model.*).
// Usati dal mock bulk /relationships/:groupId/activities per proiettare ActivitySummary.
const ACTIVITY_FQN = {
  TASK: 'it.dvel.crm.core.model.Task',
  NOTE: 'it.dvel.crm.core.model.Note',
  CALL: 'it.dvel.crm.core.model.Call',
  EMAIL: 'it.dvel.crm.core.model.Email',
  MEETING: 'it.dvel.crm.core.model.Meeting',
  LINKEDIN: 'it.dvel.crm.core.model.LinkedinPost',
  MESSAGE: 'it.dvel.crm.core.model.Message',
} as const;

// ─── Convenzione helper di questo file ─────────────────────────────────
// Alcuni helper sono "transform" — costruiscono e ritornano un nuovo
// valore (`enrichMessageInfos`, `applySortSpec`). Altri sono "resolve" /
// "normalize" — mutano in place una struttura passata dal chiamante
// (`resolvePersonSourcePatch`). Lo split e' deliberato:
//   - transform: input read-only, output nuovo (sicuro da chain-are);
//   - resolve: input gia' "in costruzione" che il chiamante sta riempiendo,
//     muta-zione coerente con come il backend Java muta l'entity prima
//     di persisterla (specchio della semantica server).
// Quando si aggiunge un nuovo helper, scegliere lo stile in base alla
// responsabilita': se cambia significato dei dati a partire dall'esterno
// (resolve, find-or-create, cascade), in-place. Se produce una vista
// derivata (sort, enrich, project), return new.

/**
 * Applica il parametro `sort` Liferay (es. `"expirationDate:desc"` o
 * multi-campo `"priority:asc,createDate:desc"`) a una lista mock.
 * Centralizzato per Queue/Task/Business/Person/Message: tutti i loro
 * GET list rispettano la stessa sintassi del backend Vulcan.
 *
 * null-safe: i valori null ordinano prima (asc) / dopo (desc).
 * Identita' (a === b) passa al campo successivo per sort multi-livello.
 */
function applySortSpec<T>(items: T[], sort: string | null): T[] {
  if (!sort) return items;
  const specs = sort.split(',').map((s) => {
    const [field, order] = s.split(':');
    return { field: field.trim(), asc: (order ?? 'asc').trim().toLowerCase() !== 'desc' };
  });
  return [...items].sort((a, b) => {
    for (const { field, asc } of specs) {
      const av = (a as unknown as Record<string, unknown>)[field];
      const bv = (b as unknown as Record<string, unknown>)[field];
      if (av === bv) continue;
      if (av == null) return asc ? -1 : 1;
      if (bv == null) return asc ? 1 : -1;
      return (av < bv ? -1 : 1) * (asc ? 1 : -1);
    }
    return 0;
  });
}

/**
 * Envelope `Page<T>` del mock: applica lo slice di paginazione a `all` (già filtrato/ordinato),
 * mappa la sola pagina via `toDto`, e ritorna items/page/pageSize/totalCount/lastPage più i campi
 * `actions`/`facets` che Vulcan include sempre. Gemello di `applySortSpec` per la parte envelope.
 */
function buildPage<TIn, TOut>(all: TIn[], page: number, pageSize: number, toDto: (x: TIn) => TOut) {
  const totalCount = all.length;
  const slice = all.slice((page - 1) * pageSize, page * pageSize);
  return {
    items: slice.map(toDto),
    page,
    pageSize,
    totalCount,
    lastPage: Math.max(1, Math.ceil(totalCount / pageSize)),
    actions: {},
    facets: [],
  };
}

/**
 * Materializza l'albero di configurazione (`conditionGroups`/`actions`) dagli `*Input` del payload
 * al modello con id, che è ciò che i GET successivi restituiscono: senza questo passaggio il
 * record salvato terrebbe la sola testata e in edit i blocchi SE e ALLORA si ripresenterebbero
 * vuoti.
 *
 * SEMANTICA PER RAMO, come il server (`AutomationDefinitionLocalServiceImpl.saveConfiguration`):
 * ramo ASSENTE (`undefined`) lascia invariato quello del record, ramo VUOTO (`[]`) lo azzera, ramo
 * valorizzato lo sostituisce. Consente a una PATCH sui soli scalari di non distruggere la
 * configurazione, e a una PATCH su un ramo solo di non toccare l'altro.
 *
 * DIVERGENZA DICHIARATA — qui si ricrea sempre, il server no. Sul ramo AZIONI il server allinea
 * per-diff sull'`automationActionId` che il payload porta: un'azione già esistente si aggiorna in
 * place conservando il suo id, e con esso le esecuzioni che gli pendono sotto per chiave esterna;
 * solo le nuove (id 0) si creano e solo le sparite si cancellano. Il mock assegna id nuovi a tutte
 * le azioni a ogni salvataggio. In dev la differenza non si vede — le esecuzioni non esistono nel
 * mondo mock — ma se un giorno ci saranno, questa è la funzione da riscrivere.
 */
function applyMockAutomationConfiguration(
  record: Automation,
  input: Pick<AutomationCreatePayload, 'conditionGroups' | 'actions'>,
): void {
  if (input.conditionGroups !== undefined) {
    record.conditionGroups = input.conditionGroups.map((group) => ({
      automationConditionGroupId: nextId++,
      priority: group.priority,
      conditions: group.conditions.map((condition) => ({
        automationConditionId: nextId++,
        fieldName: condition.fieldName,
        fieldType: condition.fieldType,
        operator: condition.operator,
        value: condition.value,
        valueSource: condition.valueSource,
        priority: condition.priority,
      })),
    }));
  }

  // Campo per campo e non con lo spread, di proposito: il builder manda nell'input anche
  // l'`automationActionId` (0 per un'azione nuova, l'id vero per una già salvata), e uno spread lo
  // porterebbe dentro sovrascrivendo quello appena assegnato — riportando in dev azioni con id 0.
  if (input.actions !== undefined) {
    record.actions = input.actions.map((action) => ({
      automationActionId: nextId++,
      code: action.code,
      title: action.title,
      config: action.config,
      onError: action.onError,
      retryCount: action.retryCount,
      retryDelaySeconds: action.retryDelaySeconds,
      priority: action.priority,
      enabled: action.enabled,
    }));
  }
}

/**
 * Codice esterno: la stringa vuota diventa `null`.
 *
 * Non è un vezzo del mock, è la regola del backend: l'unique è su
 * `(groupId, externalCode)` e in MySQL più `NULL` convivono mentre più `''` no.
 * Senza questa normalizzazione, il secondo preventivo salvato senza codice
 * violerebbe l'indice.
 */
function normalizeExternalCode(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Validazioni del preventivo, con gli stessi `type` degli exception mapper del
 * backend. `current` è `null` in creazione: i campi obbligatori si pretendono
 * solo lì, perché in PATCH un campo assente mantiene il valore corrente.
 */
function validateQuotationInput(
  input: QuotationInput,
  current: Quotation | null,
  groupId: number,
): Response | null {
  const isCreate = current === null;

  if (isCreate || input.businessId !== undefined) {
    if (!(Number(input.businessId ?? 0) > 0)) {
      return HttpResponse.json(
        { title: 'L\'azienda di riferimento e\' obbligatoria', type: 'MandatoryBusinessId' },
        { status: 400 }
      );
    }
  }
  if (isCreate || input.title !== undefined) {
    const title = (input.title ?? '').trim();
    if (!title) {
      return HttpResponse.json(
        { title: 'Il titolo del preventivo e\' obbligatorio', type: 'MandatoryTitle' },
        { status: 400 }
      );
    }
    if (title.length > 255) {
      return HttpResponse.json(
        { title: 'Il titolo supera i 255 caratteri', type: 'TitleTooLong' },
        { status: 400 }
      );
    }
  }
  if (input.notes != null && input.notes.length > 4000) {
    return HttpResponse.json(
      { title: 'Le note superano i 4000 caratteri', type: 'NotesTooLong' },
      { status: 400 }
    );
  }
  // In creazione lo status è ignorato, quindi non si valida: validarlo darebbe
  // un 400 su un valore che il backend non guarda nemmeno.
  //
  // Il controllo passa dalla tabella del mapper e non da un intervallo scritto
  // qui: un range hardcoded resterebbe indietro alla prima rinumerazione, ed e'
  // esattamente cio' che è appena successo agli stati.
  //
  // Ammette anche gli stati CONTRIBUITI dai plugin, come fa il backend via
  // QuotationVocabularyManager: un mock più severo del server farebbe fallire
  // in mock mode un salvataggio che in produzione passa.
  if (!isCreate && input.status !== undefined) {
    if (!isValidQuotationStatus(input.status)) {
      return HttpResponse.json(
        { title: `Stato ${input.status} non ammesso`, type: 'InvalidStatus' },
        { status: 400 }
      );
    }
  }
  const externalCode = normalizeExternalCode(input.externalCode);
  if (externalCode !== null && input.externalCode !== undefined) {
    const duplicate = mockQuotations.some(
      (q) =>
        q.quotationId !== current?.quotationId &&
        q.groupId === (current?.groupId ?? MOCK_GROUP_ID) &&
        q.externalCode === externalCode
    );
    if (duplicate) {
      return HttpResponse.json(
        {
          title: `Preventivo con codice '${externalCode}' già esistente nel sito`,
          type: 'DuplicateQuotationExternalCode',
        },
        { status: 409 }
      );
    }
  }
  // TS-101: stato di pipeline. Specchio del resource backend:
  //   - se fornito (>0) deve esistere tra gli stati delle pipeline del gruppo;
  //   - obbligatorio se il gruppo ha ≥1 pipeline (in creazione, e in update quando
  //     il campo è inviato). Senza pipeline resta ammesso 0.
  if (isCreate || input.currentStateId !== undefined) {
    const stateId = Number(input.currentStateId ?? 0);
    const groupPipelines = mockPipelines.filter((p) => p.groupId === groupId);
    if (stateId > 0) {
      const exists = groupPipelines.some((p) =>
        p.states.some((s) => s.pipelineStateId === stateId),
      );
      if (!exists) {
        return HttpResponse.json(
          // Il tipo e' quello vero: la validazione vive nel servizio della collocazione e vale per
          // tutte le entita', quindi l'errore e' della pipeline e non del preventivo.
          { title: 'Lo stato di pipeline indicato non esiste', type: 'PipelineException.InvalidState' },
          { status: 400 },
        );
      }
    } else if (groupPipelines.length > 0) {
      return HttpResponse.json(
        { title: 'Selezionare uno stato di pipeline', type: 'MandatoryCurrentState' },
        { status: 400 },
      );
    }
  }
  return null;
}

/**
 * Riconciliazione aggregata degli stati di una pipeline, specchio di
 * `PipelineLocalServiceImpl._reconcileStates`: gli stati con `pipelineStateId`
 * noto sono aggiornati (id preservato), quelli senza prendono un nuovo id dal
 * contatore condiviso, quelli non più presenti spariscono, e la `position` è
 * l'indice 1-based nell'array (l'ordine del form, drag&drop compreso).
 */
function reconcileMockPipelineStates(
  existing: PipelineState[],
  incoming: PipelineStateInput[],
  pipelineId: number,
  groupId: number,
  now: string,
): PipelineState[] {
  // Parità col backend (B3): uno stato rimosso (in `existing` ma non più in `incoming`) sgancia
  // i preventivi che vi puntano -> currentStateId = 0, così non restano "dangling".
  const keptIds = new Set(
    incoming.map((s) => s.pipelineStateId).filter((x): x is number => !!x),
  );
  for (const e of existing) {
    if (!keptIds.has(e.pipelineStateId)) {
      for (const q of mockQuotations) {
        if (q.currentStateId === e.pipelineStateId) q.currentStateId = 0;
      }
    }
  }

  return incoming.map((s, i) => {
    const prev = s.pipelineStateId
      ? existing.find((e) => e.pipelineStateId === s.pipelineStateId)
      : undefined;
    return {
      pipelineStateId: prev ? prev.pipelineStateId : nextId++,
      pipelineId,
      code: s.code,
      description: s.description ?? null,
      kind: KIND_FROM_INT[s.kind ?? 0] ?? 'normal',
      position: i + 1,
      companyId: 1,
      groupId,
      userId: 1,
      userName: prev?.userName ?? 'Sistema',
      createDate: prev?.createDate ?? now,
      modifiedDate: now,
    };
  });
}

/**
 * Validazioni della pipeline, con gli stessi `type` degli exception mapper del
 * backend. `states` è sempre obbligatorio (schema `required` + il resource
 * lancia BadRequest se assente), e deve avere esattamente uno stato iniziale e
 * almeno uno finale (vinto o perso), codici non vuoti e non duplicati.
 */
function validatePipelineInput(input: PipelineInput, current: Pipeline | null): Response | null {
  const isCreate = current === null;

  if (isCreate || input.name !== undefined) {
    const name = (input.name ?? '').trim();
    if (!name)
      return HttpResponse.json({ title: 'Il nome è obbligatorio', type: 'MandatoryName' }, { status: 400 });
    if (name.length > 255)
      return HttpResponse.json({ title: 'Il nome supera i 255 caratteri', type: 'NameTooLong' }, { status: 400 });
  }

  // TS-117: il tipo è obbligatorio in creazione e ignorato in aggiornamento (non è modificabile).
  // Il mock lo rifiuta come il backend: se fosse più permissivo, in modalità mock si creerebbe un
  // funnel senza tipo e il difetto si scoprirebbe solo sul server vero.
  if (isCreate && !(input.className ?? '').trim())
    return HttpResponse.json(
      { title: 'Il tipo di entità è obbligatorio', type: 'MandatoryClassName' }, { status: 400 });

  const states = input.states;
  if (states === undefined)
    return HttpResponse.json({ title: "Il campo 'states' è obbligatorio", type: 'MandatoryStates' }, { status: 400 });
  if (states.length === 0)
    return HttpResponse.json({ title: 'Una pipeline deve avere almeno uno stato', type: 'NoStates' }, { status: 400 });

  let initialCount = 0;
  let finalCount = 0;
  const codes = new Set<string>();
  for (const s of states) {
    const code = (s.code ?? '').trim();
    if (!code)
      return HttpResponse.json({ title: 'Il codice stato è obbligatorio', type: 'MandatoryStateCode' }, { status: 400 });
    if (codes.has(code.toLowerCase()))
      return HttpResponse.json({ title: `Il codice stato '${code}' è duplicato`, type: 'DuplicateStateCode' }, { status: 400 });
    codes.add(code.toLowerCase());
    // kind: 1=iniziale, 2=vinto, 3=perso (enum PipelineStateKind). Un solo campo => una sola
    // caratteristica per stato (niente check "iniziale e finale insieme").
    if (s.kind === 1) initialCount++;
    if (s.kind === 2 || s.kind === 3) finalCount++;
  }
  if (initialCount !== 1)
    return HttpResponse.json({ title: 'Una pipeline deve avere esattamente uno stato iniziale', type: 'InvalidInitialCount' }, { status: 400 });
  if (finalCount < 1)
    return HttpResponse.json({ title: 'Una pipeline deve avere almeno uno stato finale (vinto o perso)', type: 'InvalidFinalCount' }, { status: 400 });

  return null;
}

/**
 * Risolve il dual-field PersonSource su un patch parziale di Person.
 * Replica la logica del backend (PeopleResourceImpl + PersonSourceLocalService):
 *
 *   - se `personSourceId > 0` -> verifica esistenza in mockPersonSources,
 *     allinea `personSource` al name della source (denormalizzazione).
 *     Se id non esiste, azzera entrambi (coerente con cascade zero-out).
 *   - altrimenti se `personSource` non vuoto -> find-or-create case-insensitive
 *     su finder G_N. Se non esiste, append nuovo record con id auto-incrementato.
 *     Setta entrambi i campi sulla Person.
 *   - altrimenti se `personSourceId === 0` -> reset esplicito (nessuna source).
 *
 * Muta il patch in place. Da chiamare DOPO personInputFromDto e PRIMA
 * di applicare il patch al record mock.
 */
function resolvePersonSourcePatch(patch: Partial<Person>): void {
  if (patch.personSourceId !== undefined && patch.personSourceId > 0) {
    const found = mockPersonSources.find((s) => s.personSourceId === patch.personSourceId);
    if (found) {
      patch.personSource = found.name;
    } else {
      patch.personSourceId = 0;
      patch.personSource = '';
    }
    return;
  }
  if (patch.personSource !== undefined && patch.personSource.trim() !== '') {
    const needle = patch.personSource.toLowerCase();
    let found = mockPersonSources.find((s) => s.name.toLowerCase() === needle);
    if (!found) {
      const now = new Date().toISOString();
      found = {
        personSourceId: consumeNextPersonSourceId(),
        name: patch.personSource,
        companyId: 1,
        groupId: MOCK_GROUP_ID,
        userId: 1,
        userName: 'Sistema',
        createDate: now,
        modifiedDate: now,
      };
      mockPersonSources.push(found);
    }
    patch.personSourceId = found.personSourceId;
    patch.personSource = found.name;
    return;
  }
  if (patch.personSourceId === 0) {
    patch.personSource = '';
  }
}

/**
 * Clone esatto di resolvePersonSourcePatch per il campo channel.
 */
function resolvePersonChannelPatch(patch: Partial<Person>): void {
  if (patch.personChannelId !== undefined && patch.personChannelId > 0) {
    const found = mockPersonChannels.find((c) => c.personChannelId === patch.personChannelId);
    if (found) {
      patch.personChannel = found.name;
    } else {
      patch.personChannelId = 0;
      patch.personChannel = '';
    }
    return;
  }
  if (patch.personChannel !== undefined && patch.personChannel.trim() !== '') {
    const needle = patch.personChannel.toLowerCase();
    let found = mockPersonChannels.find((c) => c.name.toLowerCase() === needle);
    if (!found) {
      const now = new Date().toISOString();
      found = {
        personChannelId: consumeNextPersonChannelId(),
        name: patch.personChannel,
        companyId: 1,
        groupId: MOCK_GROUP_ID,
        userId: 1,
        userName: 'Sistema',
        createDate: now,
        modifiedDate: now,
      };
      mockPersonChannels.push(found);
    }
    patch.personChannelId = found.personChannelId;
    patch.personChannel = found.name;
    return;
  }
  if (patch.personChannelId === 0) {
    patch.personChannel = '';
  }
}

/**
 * Utenti mock per il lookup `assignedUserId` nei Task. Shape allineata
 * al MemberDto del backend Liferay (vedi userApi.ts): il client mappa
 * userId/firstName/lastName/emailAddress -> User UI. Gli userId 1-4
 * matchano con il `userId` usato negli audit fields
 * (mockPersons/mockTasks/mockBusinesses).
 */
const MOCK_USERS = [
  { userId: 1, screenName: 'jader.francia', firstName: 'Jader', lastName: 'Francia', emailAddress: 'jaderfrancia@d-vel.com', portraitURL: null, role: 'ADMINISTRATOR' },
  { userId: 2, screenName: 'michele.placido', firstName: 'Michele', lastName: 'Placido', emailAddress: 'mick.placido@gmail.com', portraitURL: null, role: 'USER' },
  { userId: 3, screenName: 'marco.zanetti', firstName: 'Marco', lastName: 'Zanetti', emailAddress: 'marco.zanetti@trentinosicurezza.it', portraitURL: null, role: 'USER' },
  { userId: 4, screenName: 'francesca.ruocco', firstName: 'Francesca', lastName: 'Ruocco', emailAddress: 'francescaruocco@trentinosicurezza.it', portraitURL: null, role: 'USER' },
];

/**
 * MemberDto di un utente mock (per i membri dei team). Risolve contro MOCK_USERS
 * — unica sorgente degli utenti mock — e aggiunge `status: 0` (APPROVED), che
 * MOCK_USERS non porta ma che il MemberDto prevede.
 */
function mockMemberDto(userId: number): MemberDto | null {
  const u = MOCK_USERS.find((x) => x.userId === userId);
  return u ? { ...u, status: 0 } : null;
}

/** Team mock -> TeamDto del backend (membri risolti da memberIds). */
function toTeamDto(t: MockTeam) {
  return {
    teamId: t.teamId,
    groupId: t.groupId,
    name: t.name,
    description: t.description,
    members: t.memberIds
      .map(mockMemberDto)
      .filter((m): m is MemberDto => m !== null),
  };
}

/**
 * Titolo leggibile di un'entità target (FQN + classPK), per le proiezioni
 * from-contributors / contributor-records. Risolve le entità principali
 * associabili (Person/Business/Quotation) contro i rispettivi mock, con
 * fallback `<ShortName> #<pk>` per i tipi non risolti.
 */
function resolveEntityTitle(fqn: string, pk: number): string {
  if (fqn.endsWith('.Person')) {
    const p = mockPersons.find((x) => x.personId === pk);
    return p ? `${p.firstName} ${p.lastName}`.trim() : `Persona #${pk}`;
  }
  if (fqn.endsWith('.Business')) {
    const b = mockBusinesses.find((x) => x.businessId === pk);
    return b ? b.name : `Azienda #${pk}`;
  }
  if (fqn.endsWith('.Quotation')) {
    const q = mockQuotations.find((x) => x.quotationId === pk);
    return q ? q.title : `Preventivo #${pk}`;
  }
  const short = fqn.split('.').pop() ?? 'Record';
  return `${short} #${pk}`;
}

/**
 * Proietta le relazioni DIRETTE di un'entità (className, classPK come lato 1),
 * raggruppate per FQN target, nella shape RelationshipsFromContributors.
 * `relationshipIdFor` decide l'id di ciascuna entry: l'id reale della relazione
 * per from-contributors, 0 per la preview (la relazione non esiste ancora).
 */
function buildFromContributors(
  className: string,
  classPK: number,
  relationshipIdFor: (relationshipId: number) => number,
) {
  const byFqn = new Map<
    string,
    { relationshipId: number; classPK: number; title: string; description: string | null }[]
  >();
  for (const r of mockRelationships) {
    if (r.className1 === className && r.classPK1 === classPK) {
      if (!byFqn.has(r.className2)) byFqn.set(r.className2, []);
      byFqn.get(r.className2)!.push({
        relationshipId: relationshipIdFor(r.relationshipId),
        classPK: r.classPK2,
        title: resolveEntityTitle(r.className2, r.classPK2),
        description: r.description,
      });
    }
  }
  const contributors = [...byFqn.entries()].map(([fqn, entries]) => ({
    className: fqn,
    total: entries.length,
    entries,
  }));
  return {
    contributors,
    totalEntries: contributors.reduce((acc, c) => acc + c.total, 0),
  };
}

/**
 * L'utente del login finto: i mock che scrivono «chi l'ha fatto» usano lui. È l'utente 1 di
 * MOCK_USERS, quello degli audit e delle assegnazioni, che i seed SQL fanno diventare l'utente di test.
 */
export const mockLoginUser = {
  uuid: '41c7bd44-f258-fbb2-eb2b-916e445beabc',
  userId: 1,
  screenName: 'jader.francia',
  emailAddress: 'jaderfrancia@d-vel.com',
  firstName: 'Jader',
  lastName: 'Francia',
  fullName: 'Jader Francia',
  portraitURL: '',
  companyLogoURL: '',
  companyName: "D'vel",
  languageId: 'it_IT',
  timeZone: 'Europe/Rome',
  passwordReset: false,
  administrator: true,
  groups: [{ groupId: MOCK_GROUP_ID, name: 'Guest', description: '' }],
  defaultGroupId: MOCK_GROUP_ID,
  // I plugin cliente abilitati per l'utente finto. Contro il server questa lista la
  // compone DTOConverterUtil incrociando le righe PluginEnabling della company con i
  // CRMModule registrati; in mock la si dichiara qui. Un plugin che non compare in
  // questo elenco viene filtrato da useEnabledPlugins e sparisce INTERO - rotte,
  // voci di menu e slot - senza nessun errore.
  plugins: [
    'PLUGIN_TRENTINO_SICUREZZA_QUOTATION',
    'PLUGIN_FRATELLI_POLLINI_QUOTATION',
    'PLUGIN_F2_DIAGNOSTIC',
  ],
  // FILO-53: le voci del core spente. Contro il server le scrive la pagina «Voci del CRM».
  disabledCoreItems: [],
  // FILO-64: la versione del CRM; contro il server arriva da PlatformVersion in crm-util.
  platformVersion: '1.0.0',
  platformReleaseDate: '2026-10-29',
};

/** Il task con quell'id, se l'utente del mock lo vede: un task altrui è 404, come nel backend (FILO-41). */
const visibleTask = (id: number) => mockTasks.find((t) => t.taskId === id && mockTaskVisible(t, mockLoginUser));
const visibleTaskIndex = (id: number) => {
  const task = visibleTask(id);
  return task ? mockTasks.indexOf(task) : -1;
};

/**
 * Mock LoginResponse conforme allo shape del backend CRM reale
 * (vedi AUTH_LOGIN_GUIDE.md sezione 9 per i contratti). Usato dai
 * handler /o/crm/v1_0/user/login, /user/refresh e /user/init.
 */
const MOCK_LOGIN_RESPONSE = {
  user: mockLoginUser,
  accessToken: 'mock-access-token',
  refreshToken: 'mock-refresh-token',
  expiresIn: 600,
};

/**
 * Handler auth: CRM user endpoints (/user/login, /user/refresh, ecc.).
 * Esportati separatamente per permettere modalita' mock ibrida — vedi
 * mockWorker.ts + VITE_USE_MOCK_AUTH. Quando VITE_USE_MOCK_AUTH=false,
 * questi handler NON vengono registrati e le chiamate /o/crm/v1_0/user/*
 * passano al Vite proxy -> Liferay reale.
 */
export const coreAuthHandlers = [
  // Mock smart: valida credenziali per permettere test sad-path (credenziali
  // errate -> 403) senza passare a real mode. Accetta solo test@liferay.com /
  // test; qualsiasi altra combinazione ritorna 403 con la stessa shape di
  // errore del backend reale ({status, title, type}).
  http.post('/o/crm/v1_0/user/login', async ({ request }) => {
    const body = (await request.json()) as {
      webId?: string;
      emailAddress?: string;
      password?: string;
    };
    // Trigger sentinel per smoke-testare il flusso 412 (NoSuchCompanyException
    // Liferay) in dev/mock mode senza dover toccare il backend reale. Allinea
    // la shape della response al backend (mapper NoSuchWebIdExceptionMapper).
    // Vedi LoginPage + authSlice: la response 412 fa apparire il campo Istanza.
    if (body?.webId === 'invalid' || !body?.webId) {
      return HttpResponse.json(
        {
          status: 'PRECONDITION_FAILED',
          title: 'No company found for the provided webId',
          type: 'PreconditionFailed',
        },
        { status: 412 }
      );
    }
    if (body?.emailAddress === 'test@liferay.com' && body?.password === 'test') {
      return HttpResponse.json(MOCK_LOGIN_RESPONSE);
    }
    return HttpResponse.json(
      {
        status: 'FORBIDDEN',
        title: 'OAuth2 invalid_grant',
        type: 'AuthException',
      },
      { status: 403 }
    );
  }),

  http.post('/o/crm/v1_0/user/refresh', () =>
    HttpResponse.json({
      ...MOCK_LOGIN_RESPONSE,
      user: null,
      accessToken: 'mock-access-token-refreshed',
      refreshToken: 'mock-refresh-token-rotated',
    })
  ),

  http.get('/o/crm/v1_0/user/init', () => HttpResponse.json(MOCK_LOGIN_RESPONSE.user)),

  http.get('/o/crm/v1_0/user/logout', () => new HttpResponse(null, { status: 200 })),

  // FILO-70: reimpostazione della password con il link per email. Come il backend,
  // /user/resetPassword risponde 200 per qualunque indirizzo; l'unico errore è
  // l'istanza sconosciuta, con lo stesso sentinel 'invalid' del login.
  http.post('/o/crm/v1_0/user/resetPassword', async ({ request }) => {
    const body = (await request.json()) as { webId?: string };
    if (body?.webId === 'invalid' || !body?.webId) {
      return HttpResponse.json(
        { status: 'PRECONDITION_FAILED', title: 'No company found for the provided webId', type: 'PreconditionFailed' },
        { status: 412 }
      );
    }
    return new HttpResponse(null, { status: 200 });
  }),

  // Sentinel per i casi d'errore della pagina /reset-password: ticket 'expired'
  // dà il 403 del link non valido, due password diverse il 400 del mapper.
  http.post('/o/crm/v1_0/user/setPasswordByTicket', async ({ request }) => {
    const body = (await request.json()) as { ticket?: string; password1?: string; password2?: string };
    if (body?.ticket === 'expired') {
      return HttpResponse.json(
        { status: 'FORBIDDEN', title: 'Il link di reimpostazione della password non è valido o è scaduto', type: 'PrincipalException' },
        { status: 403 }
      );
    }
    if (body?.password1 !== body?.password2) {
      return HttpResponse.json(
        { status: 'BAD_REQUEST', title: 'Le password che hai inserito non corrispondono. Inserisci di nuovo la tua password.', type: 'UserPasswordException' },
        { status: 400 }
      );
    }
    return new HttpResponse(null, { status: 200 });
  }),
];

/**
 * Theme (white-label opzione C): simula il backend che, dato il tenant del
 * JWT, ritorna la sua BrandPalette. Qui il "tenant" è dedotto dal dominio
 * email dell'utente mock (MOCK_LOGIN_RESPONSE). Dominio non mappato → null
 * (il client applica la palette di default).
 *
 * Le palette sono FIXTURE del mock (nel backend reale starebbero su DB/config).
 * Per testare il tema blu in mock mode, mappare temporaneamente "example.com"
 * (dominio dell'utente mock) su "trentino-sicurezza" in MOCK_DOMAIN_TO_BRAND.
 */
// Due colori per palette: gli altri li deriva il frontend (`derivaPalette`),
// esattamente come fara' il backend quando l'endpoint sara' reale.
const MOCK_BRAND_PALETTES: Record<string, Record<string, string>> = {
  dvel: { primary: '#266c9a', secondary: '#4caf50' },
  // Palette derivata dal brand di trentinosicurezza.it (colori del logo:
  // #004C7A navy, #266C9A blu, #73A5C8 azzurro, #4D4D4D grigio).
  'trentino-sicurezza': { primary: '#266c9a', secondary: '#004c7a' },
};

const MOCK_DOMAIN_TO_BRAND: Record<string, string> = {
  'example.com': 'dvel',            // utente mock loggato → palette del concept
  'd-vel.com': 'dvel',
  'trentino-sicurezza.it': 'trentino-sicurezza',
};

/** La palette del brand dell'utente mock, dal dominio della sua email; undefined se non mappato. */
function currentMockBrandPalette(): Record<string, string> | undefined {
  const domain = MOCK_LOGIN_RESPONSE.user.emailAddress.split('@')[1]?.toLowerCase();
  const key = domain ? MOCK_DOMAIN_TO_BRAND[domain] : undefined;
  return key ? MOCK_BRAND_PALETTES[key] : undefined;
}

/**
 * Handler dati: tutte le entita' del dominio (queue, task, person,
 * business, message, relationship, user search). Separati dagli handler
 * auth per permettere il mix "auth reale + dati mock" (VITE_USE_MOCK_AUTH=false).
 */
export const coreDataHandlers = [
  ...zoneHandlers,
  ...mapPointHandlers,

  // COMPANY — logo aziendale. Il mock echeggia il base64 caricato come data URL,
  // così l'header mostra davvero il logo scelto; DELETE ripristina il default.
  http.patch(`${API}/company/logo`, async ({ request }) => {
    const body = (await request.json()) as { logoFile?: string };
    const logoURL = body.logoFile ? `data:image/jpeg;base64,${body.logoFile}` : '';
    return HttpResponse.json({ logoURL });
  }),
  http.delete(`${API}/company/logo`, () => HttpResponse.json({ logoURL: '' })),
  http.patch(`${API}/company/name`, async ({ request }) => {
    const body = (await request.json()) as { name?: string };
    return HttpResponse.json({ logoURL: '', name: (body.name ?? '').trim() });
  }),

  ...listTypeHandlers,

  // IMPOSTAZIONI DELLA COMPANY (FILO-54) — per ora i soli colori del tema. Il
  // backend parla di primaryColor/secondaryColor; le fixture restano in
  // primary/secondary e si traducono qui. Come ogni dato di questi handler vive
  // in memoria: il salvataggio "regge" per la sessione, al reload si riparte dai
  // valori di sorgente.
  //
  // Il path è quello vero di `CompanyResourceImpl.getCompanySettings()`: un
  // handler registrato su un path che nessuno chiama non fallisce, semplicemente
  // non intercetta nulla — la richiesta finisce nel passthrough e arriva a
  // Liferay, che risponde 401.
  http.get(`${API}/company/settings`, () => {
    const current = currentMockBrandPalette();
    return HttpResponse.json({
      primaryColor: current?.primary ?? '',
      secondaryColor: current?.secondary ?? '',
      rapidContactTaskRules: mockTaskRules ?? MOCK_DEFAULT_TASK_RULES,
      rapidContactTaskRulesCustom: mockTaskRules !== null,
      // FILO-75: il login dei mock è per e-mail, come il default di Liferay.
      loginType: 'EMAIL_ADDRESS',
    });
  }),

  // Le regole dei task di follow-up dei contatti rapidi (FILO-67), come CompanySettingLocalService:
  // null = valgono le predefinite.
  http.patch(`${API}/company/settings/rapid-contact-task-rules`, async ({ request }) => {
    const body = (await request.json()) as { rapidContactTaskRules?: typeof MOCK_DEFAULT_TASK_RULES };
    mockTaskRules = body.rapidContactTaskRules ?? null;
    return HttpResponse.json({ rapidContactTaskRules: mockTaskRules ?? MOCK_DEFAULT_TASK_RULES, rapidContactTaskRulesCustom: mockTaskRules !== null });
  }),
  http.delete(`${API}/company/settings/rapid-contact-task-rules`, () => {
    mockTaskRules = null;
    return HttpResponse.json({ rapidContactTaskRules: MOCK_DEFAULT_TASK_RULES, rapidContactTaskRulesCustom: false });
  }),

  // PATCH — i due colori scelti da Impostazioni → Azienda.
  http.patch(`${API}/company/settings/palette`, async ({ request }) => {
    const body = (await request.json()) as { primaryColor?: string; secondaryColor?: string };
    const current = currentMockBrandPalette() ?? MOCK_BRAND_PALETTES.dvel;
    if (body.primaryColor) current.primary = body.primaryColor;
    // Il secondario si puo' anche AZZERARE (stringa vuota): significa "derivalo
    // dal primario", quindi qui si distingue "assente" da "vuoto".
    if (body.secondaryColor !== undefined) current.secondary = body.secondaryColor;
    return HttpResponse.json({ primaryColor: current.primary, secondaryColor: current.secondary });
  }),

  // DELETE — "Ripristina colori predefiniti": il backend svuota i due colori e
  // risponde a colori VUOTI, che per il client significano "usa i tuoi
  // predefiniti". Qui si svuota la palette del brand corrente, cosi' anche il GET
  // successivo la da' per non configurata.
  http.delete(`${API}/company/settings/palette`, () => {
    const current = currentMockBrandPalette() ?? MOCK_BRAND_PALETTES.dvel;
    current.primary = '';
    current.secondary = '';
    return HttpResponse.json({ primaryColor: '', secondaryColor: '' });
  }),

  // ============================================================
  // QUEUE — path e verbi allineati a QueuesResourceImpl.java
  //   collection: /queues/{groupId}  (GET list+search, POST create)
  //   single:     /queue/{queueId}   (GET, PATCH, DELETE)
  // Il body di POST/PATCH e' un QueueInput = { name }. Gli audit
  // fields (companyId, userId, userName, create/modifiedDate) sono
  // popolati server-side; groupId e' letto dal path param.
  // Errori simulati coerenti col backend:
  //   - 400 MandatoryName: name null/vuoto
  //   - 409 DuplicateEntry: nome gia' esistente nel groupId
  //   - 404: queueId inesistente
  // ============================================================
  http.get(`${API}/queues/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockQueues;
    if (search) {
      const needle = search.toLowerCase();
      result = mockQueues.filter(
        (qu) =>
          qu.name.toLowerCase().includes(needle) ||
          (qu.description ?? '').toLowerCase().includes(needle)
      );
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, (x) => x));
  }),
  http.post(`${API}/queues/:groupId`, async ({ params, request }) => {
    const payload = (await request.json()) as { name?: string; description?: string | null };
    const name = (payload.name ?? '').trim();
    if (!name) {
      return HttpResponse.json(
        { title: 'Il nome della coda e\' obbligatorio', type: 'MandatoryName' },
        { status: 400 }
      );
    }
    const groupId = Number(params.groupId);
    const duplicate = mockQueues.some(
      (q) => q.groupId === groupId && q.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      return HttpResponse.json(
        { title: `Coda con nome '${name}' già esistente nel sito`, type: 'DuplicateEntry' },
        { status: 409 }
      );
    }
    const created = {
      name,
      description: payload.description ?? null,
      queueId: nextId++,
      companyId: 1,
      groupId,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mockQueues.push(created as any);
    // Contratto Liferay (OpenAPI): POST ritorna 200, non 201.
    return HttpResponse.json(created, { status: 200 });
  }),
  http.get(`${API}/queue/:id`, ({ params }) => {
    const item = mockQueues.find((q) => q.queueId === Number(params.id));
    return item ? HttpResponse.json(item) : new HttpResponse(null, { status: 404 });
  }),
  http.patch(`${API}/queue/:id`, async ({ params, request }) => {
    const payload = (await request.json()) as { name?: string; description?: string | null };
    const name = (payload.name ?? '').trim();
    if (!name) {
      return HttpResponse.json(
        { title: 'Il nome della coda e\' obbligatorio', type: 'MandatoryName' },
        { status: 400 }
      );
    }
    const idx = mockQueues.findIndex((q) => q.queueId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const current = mockQueues[idx];
    const duplicate = mockQueues.some(
      (q) =>
        q.queueId !== current.queueId &&
        q.groupId === current.groupId &&
        q.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      return HttpResponse.json(
        { title: `Coda con nome '${name}' già esistente nel sito`, type: 'DuplicateEntry' },
        { status: 409 }
      );
    }
    // PATCH parziale, fedele a GetterUtil.getString lato backend: null/undefined
    // mantengono il valore corrente, '' o testo lo sovrascrivono (svuota/aggiorna).
    const description =
      payload.description == null ? current.description : payload.description;
    mockQueues[idx] = { ...current, name, description, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(mockQueues[idx]);
  }),
  http.delete(`${API}/queue/:id`, ({ params }) => {
    const idx = mockQueues.findIndex((q) => q.queueId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockQueues.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // AUTOMATION — allineato a AutomationsResourceImpl.java
  //   GET    /automations/object-types/{groupId} -> Page<AutomationObjectType> (per-sito)
  //   GET    /automations/condition-types/{groupId}?className= -> Page<CatalogField> (per-sito)
  //   GET    /automations/updatable-fields/{groupId}?className= -> Page<CatalogField> (per-sito)
  //   GET    /automations/action-types/{groupId}?className= -> Page<AutomationActionType> (per-entità)
  //   GET    /automations/categories/{groupId}   -> Page<AutomationCategory>
  //   GET    /automations/{groupId}?template=&status=&search=&sort=&page=&pageSize= -> Page<AutomationDefinition>
  //   GET    /automation/{id}                    -> AutomationDefinition
  //   GET    /automation/{id}/stats              -> AutomationStats
  //   POST   /automation/{id}/publish|suspend|resume -> AutomationDefinition
  //   DELETE /automation/{id}                    -> 200
  // Nota MSW: le rotte piu' specifiche (object-types, categories, /stats, /publish...) hanno un
  // segmento in piu' quindi non collidono con /automations/:groupId e /automation/:id (match per
  // numero di segmenti). Il mock ignora il groupId e ritorna l'intero catalogo (nessuno stato di
  // abilitazione plugin lato mock).
  // ============================================================
  http.get(`${API}/automations/object-types/:groupId`, () =>
    HttpResponse.json(
      buildPage(mockAutomationObjectTypes, 1, Math.max(mockAutomationObjectTypes.length, 1), (x) => x),
    ),
  ),
  // TS-65: campi SCRIVIBILI dell'entità del QUANDO, elenco distinto dai filtrabili.
  //
  // `className` MANCANTE -> 400, come il server. Diverge di proposito dal gemello condition-types,
  // che qui sotto lo tratta come stringa vuota e risponde 200 con elenco vuoto: una chiamata che
  // dimentica il parametro deve rompersi in dev, non funzionare per caso restituendo "nessun campo".
  // className IGNOTO -> 200 con elenco vuoto (il server darebbe 400): il mock non modella quali
  // provider siano deployati né la loro abilitazione per sito.
  http.get(`${API}/automations/updatable-fields/:groupId`, ({ request }) => {
    const className = new URL(request.url).searchParams.get('className');

    if (!className) return new HttpResponse(null, { status: 400 });

    const items = mockUpdatableFieldsByClassName[className] ?? [];

    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),
  // TS-65: catalogo ALLORA — azioni eseguibili per l'entità del QUANDO, filtrate per applicabilità
  // come fa il server. SYNC con `AutomationActionType.isApplicable`, che di default risponde `true`:
  // una sola azione del catalogo la sovrascrive, `crm.update-field`, che si dichiara applicabile
  // soltanto dove l'entità ha campi scrivibili (`getUpdatableFields()` non vuoto). Qui la stessa
  // domanda si fa all'elenco che alimenta l'endpoint gemello updatable-fields, così le due risposte
  // non possono contraddirsi: un'azione «aggiorna campo» offerta su un'entità senza campi da
  // aggiornare aprirebbe una modale senza scelte.
  //
  // Il `className` resta obbligatorio anche qui, così una chiamata che se lo dimentica fallisce in
  // dev come in produzione invece di funzionare per caso.
  http.get(`${API}/automations/action-types/:groupId`, ({ request }) => {
    const className = new URL(request.url).searchParams.get('className');

    if (!className) return new HttpResponse(null, { status: 400 });

    const items = mockAutomationActionTypes.filter((actionType) =>
      actionType.code === UPDATE_FIELD_ACTION_CODE
        ? (mockUpdatableFieldsByClassName[className] ?? []).length > 0
        : true,
    );

    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),
  // TS-65: catalogo SE — campi condizione dell'oggetto scelto nel QUANDO (query className).
  http.get(`${API}/automations/condition-types/:groupId`, ({ request }) => {
    const className = new URL(request.url).searchParams.get('className') ?? '';
    const items = mockConditionTypesByClassName[className] ?? [];
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),
  // TS-65: validazione della bozza in composizione (pannello Validazione del builder). Il giudice è
  // il backend: questo mock riproduce solo la STRUTTURA (oggetto ed evento del QUANDO, almeno
  // un'azione nell'ALLORA) ed è di proposito più permissivo — serve a far girare il pannello offline,
  // non a dare un verdetto. Il merito dei valori si prova contro il server. Vedi il commento in testa
  // a validateAutomationDraft.
  http.post(`${API}/automations/:groupId/validate`, async ({ request }) => {
    const body = (await request.json()) as {
      className?: string;
      eventType?: string;
      actions?: unknown[];
    };
    const violations = validateAutomationDraft(body);
    return HttpResponse.json({
      items: violations,
      page: 1,
      pageSize: violations.length,
      totalCount: violations.length,
      lastPage: 1,
      actions: {},
      facets: [],
    });
  }),
  http.get(`${API}/automations/categories/:groupId`, () =>
    HttpResponse.json(
      buildPage(mockAutomationCategories, 1, Math.max(mockAutomationCategories.length, 1), (x) => x),
    ),
  ),
  http.get(`${API}/automations/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const template = url.searchParams.get('template');
    const statusParam = url.searchParams.get('status');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockAutomations;
    if (template !== null) result = result.filter((a) => a.template === (template === 'true'));
    if (statusParam !== null) {
      result = result.filter((a) => automationToDto(a).status === Number(statusParam));
    }
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(needle) ||
          (a.automationCategoryName?.toLowerCase().includes(needle) ?? false),
      );
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, automationToDto));
  }),
  // POST /automations/{groupId} -> crea una nuova AutomationDefinition (nasce DRAFT). Il builder
  // ("Salva bozza") manda l'intero payload: qui si materializza un record e lo si aggiunge a
  // mockAutomations, così i GET successivi lo vedono. L'albero di configurazione (condizioni/azioni)
  // lo materializza applyMockAutomationConfiguration, che assegna gli id ai figli: sulla create il
  // builder manda entrambi i rami, quindi si scrive l'albero intero.
  http.post(`${API}/automations/:groupId`, async ({ params, request }) => {
    const groupId = Number(params.groupId);
    const body = (await request.json()) as AutomationCreatePayload;

    const nextId =
      mockAutomations.reduce((max, a) => Math.max(max, a.automationDefinitionId), 0) + 1;
    const category = mockAutomationCategories.find(
      (c) => c.automationCategoryId === body.automationCategoryId,
    );
    const now = new Date().toISOString();

    const created: Automation = {
      automationDefinitionId: nextId,
      name: body.name,
      description: body.description ?? null,
      className: body.className,
      automationCategoryId: body.automationCategoryId,
      automationCategoryName: category?.name ?? null,
      eventType: body.eventType,
      eventConfig: body.eventConfig ?? null,
      // Default false come il backend: il riscatto è una scelta esplicita di chi configura,
      // e riguarda i soli eventi schedulati.
      repeatable: body.repeatable ?? false,
      logEveryRecord: body.logEveryRecord ?? false,
      priority: body.priority ?? 0,
      template: body.template ?? false,
      status: 'draft',
      version: 1,
      mainAutomationDefinitionId: nextId,
      conditionGroups: [],
      actions: [],
      companyId: 1,
      groupId,
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
    };

    applyMockAutomationConfiguration(created, body);

    mockAutomations.push(created);
    return HttpResponse.json(automationToDto(created));
  }),
  // PATCH /automation/{id} -> aggiorna una AutomationDefinition esistente (edit). Solo i campi
  // presenti nel body vengono toccati, e l'albero di configurazione segue la stessa regola ramo per
  // ramo: la tiene applyMockAutomationConfiguration, insieme alla divergenza dichiarata sugli id.
  http.patch(`${API}/automation/:id`, async ({ params, request }) => {
    const record = mockAutomations.find((a) => a.automationDefinitionId === Number(params.id));
    if (!record) return new HttpResponse(null, { status: 404 });

    const body = (await request.json()) as Partial<AutomationCreatePayload>;

    if (body.name !== undefined) record.name = body.name;
    if (body.description !== undefined) record.description = body.description ?? null;
    if (body.className !== undefined) record.className = body.className;
    if (body.eventType !== undefined) record.eventType = body.eventType;
    if (body.eventConfig !== undefined) record.eventConfig = body.eventConfig ?? null;
    if (body.repeatable !== undefined) record.repeatable = body.repeatable;
    if (body.logEveryRecord !== undefined) record.logEveryRecord = body.logEveryRecord;
    if (body.priority !== undefined) record.priority = body.priority;
    if (body.template !== undefined) record.template = body.template;
    if (body.automationCategoryId !== undefined) {
      record.automationCategoryId = body.automationCategoryId;
      record.automationCategoryName =
        mockAutomationCategories.find(
          (c) => c.automationCategoryId === body.automationCategoryId,
        )?.name ?? null;
    }

    applyMockAutomationConfiguration(record, body);

    record.modifiedDate = new Date().toISOString();

    return HttpResponse.json(automationToDto(record));
  }),
  http.get(`${API}/automation/:id/stats`, ({ params }) => {
    const id = Number(params.id);
    return HttpResponse.json(
      mockAutomationStats[id] ?? {
        automationDefinitionId: id,
        runCount: 0,
        successCount: 0,
        failureCount: 0,
        lastRunDate: null,
        averageDuration: 0,
      },
    );
  }),
  http.get(`${API}/automation/:id/runs`, ({ params, request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    const all = mockAutomationRuns[Number(params.id)] ?? [];
    return HttpResponse.json(buildPage(all, page, pageSize, automationRunToDto));
  }),
  // Cancellazione di UNA passata. Il 400 sul run ancora vivo guarda il solo stato, mentre il
  // server guarda l'ETÀ e cancella volentieri un RUNNING appeso da ore: divergenza dichiarata,
  // perché i run finti non invecchiano e riprodurre la soglia non proverebbe niente in più.
  http.delete(`${API}/automation/:id/runs/:runId`, ({ params }) => {
    const runs = mockAutomationRuns[Number(params.id)];
    if (!runs) return new HttpResponse(null, { status: 404 });

    const idx = runs.findIndex((r) => r.automationRunId === Number(params.runId));
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    if (runs[idx].status === 'running') return new HttpResponse(null, { status: 400 });

    runs.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),
  // Pulizia delle passate senza trattamenti. Il criterio del server è «né AutomationExecution né
  // AutomationQueueItem figli», che qui non si può valutare: né gli esiti per record né la coda
  // sono mockati affatto. Il proxy
  // è processedItems a zero, che nel seed dice la stessa cosa — divergenza dichiarata: sul server
  // un run con record esaminati ma nessuno trattato ha processedItems > 0 e nessuna execution,
  // quindi sarebbe cancellato mentre qui sopravvive.
  // Le passate ancora VIVE si saltano invece di far fallire, come sul server, e per questo non
  // c'è nessun 400: è la differenza dichiarata fra questa e la cancellazione singola.
  http.delete(`${API}/automation/:id/runs`, ({ params }) => {
    const runs = mockAutomationRuns[Number(params.id)];
    if (!runs) return new HttpResponse(null, { status: 404 });

    for (let i = runs.length - 1; i >= 0; i--) {
      if (runs[i].processedItems === 0 && runs[i].status !== 'running') runs.splice(i, 1);
    }

    return new HttpResponse(null, { status: 200 });
  }),
  http.get(`${API}/automation/:id`, ({ params }) => {
    const a = mockAutomations.find((x) => x.automationDefinitionId === Number(params.id));
    if (!a) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(automationToDto(a));
  }),
  // Pubblicazione: tre passaggi, nello stesso ordine del server
  // (AutomationDefinitionLocalServiceImpl.publishAutomationDefinition).
  //
  // 1. STATO, e prima della validazione: chi prova a pubblicare un record già attivo deve sentirsi
  //    dire che non è una bozza, non che cosa gli manca.
  // 2. MERITO: il server applica validatePublishable e rifiuta con 400, portando le violazioni nel
  //    `detail` del Problem sotto un `title` che è un CODICE e non un testo — lo riconosce
  //    extractPublishViolations in automationThunks, che poi mostra i messaggi del detail. Qui il
  //    giudice è validateAutomationDraft, lo stesso del pannello di validazione, e da lui questo
  //    endpoint eredita la permissività dichiarata: struttura sì, merito dei parametri no. Il mock
  //    rifiuta quindi MENO del server, mai di più — che è il verso giusto in cui sbagliare.
  // 3. FAMIGLIA: attivando la versione N il server archivia le versioni precedenti della stessa
  //    famiglia (updateStatus), ed è da lì che discende l'invariante «un solo record attivo per
  //    famiglia». Senza questo passaggio in dev l'invariante non varrebbe, e il client potrebbe
  //    presupporre in buona fede una lista che il server non produce mai.
  http.post(`${API}/automation/:id/publish`, ({ params }) => {
    const a = mockAutomations.find((x) => x.automationDefinitionId === Number(params.id));
    if (!a) return new HttpResponse(null, { status: 404 });
    if (a.status !== 'draft') return new HttpResponse(null, { status: 400 });

    const violations = validateAutomationDraft(a);

    if (violations.length > 0)
      return HttpResponse.json(
        { title: 'the-automation-cannot-be-published', detail: JSON.stringify(violations) },
        { status: 400 },
      );

    const now = new Date().toISOString();

    for (const other of mockAutomations) {
      if (
        other.mainAutomationDefinitionId === a.mainAutomationDefinitionId &&
        other.version < a.version &&
        other.status !== 'archived'
      ) {
        other.status = 'archived';
        other.modifiedDate = now;
      }
    }

    a.status = 'active';
    a.modifiedDate = now;
    return HttpResponse.json(automationToDto(a));
  }),
  http.post(`${API}/automation/:id/suspend`, ({ params }) => {
    const a = mockAutomations.find((x) => x.automationDefinitionId === Number(params.id));
    if (!a) return new HttpResponse(null, { status: 404 });
    if (a.status !== 'active') return new HttpResponse(null, { status: 400 });
    a.status = 'suspended';
    a.modifiedDate = new Date().toISOString();
    return HttpResponse.json(automationToDto(a));
  }),
  http.post(`${API}/automation/:id/resume`, ({ params }) => {
    const a = mockAutomations.find((x) => x.automationDefinitionId === Number(params.id));
    if (!a) return new HttpResponse(null, { status: 404 });
    if (a.status !== 'suspended') return new HttpResponse(null, { status: 400 });
    a.status = 'active';
    a.modifiedDate = new Date().toISOString();
    return HttpResponse.json(automationToDto(a));
  }),
  // Cancellazione: il server rifiuta un'automazione ATTIVA con 400 (ActiveNotDeletable, mappato da
  // AutomationDefinitionExceptionMapper) e chi la vuole togliere la sospende prima. I TEMPLATE
  // sono esclusi dal vincolo, e non per comodità: un template attivo non sta girando — è un
  // blueprint pubblicato in gallery — e la sua view è l'unico posto da cui eliminarlo.
  // Il messaggio è quello della chiave an-active-automation-cannot-be-deleted-suspend-it-first
  // resa in italiano, come farebbe il converter per it_IT: extractError mostra il `title`, quindi
  // un codice qui finirebbe a video così com'è.
  http.delete(`${API}/automation/:id`, ({ params }) => {
    const idx = mockAutomations.findIndex((x) => x.automationDefinitionId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const a = mockAutomations[idx];

    if (!a.template && a.status === 'active')
      return HttpResponse.json(
        { title: "Un'automazione attiva non può essere eliminata: sospendila prima." },
        { status: 400 },
      );

    mockAutomations.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // TASK — allineato a TasksResourceImpl.java
  //   GET    /tasks/{groupId}?search=&sort=&page=&pageSize=&kanbanId=&expirationDateFrom=&expirationDateTo= -> Page<TaskDto>
  //   POST   /tasks/{groupId}  TaskInput                    -> TaskDto
  //   GET    /task/{taskId}                                  -> TaskDto
  //   PATCH  /task/{taskId}   TaskInput (NO status)          -> TaskDto
  //   PATCH  /task/{taskId}/status  TaskStatusInput          -> TaskDto
  //   DELETE /task/{taskId}                                  -> 200
  // Il backend non filtra per status/type/queueId via query param: quei
  // filtri sono client-side (vedi commento in TaskList).
  // ============================================================
  http.get(`${API}/tasks/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    const kanbanId = Number(url.searchParams.get('kanbanId') ?? 0);

    // La visibilità dei plugin (FILO-41), come TaskVisibilityContributor nel backend.
    let result = mockTasks.filter((t) => mockTaskVisible(t, mockLoginUser));

    // kanbanId = i task della bacheca, cioe' quelli collocati in uno degli stati della sua
    // pipeline. Ha precedenza sugli altri filtri, come lato server. Bacheca inesistente o di un
    // ALTRO tipo => elenco vuoto, non tutti i task: e' la stessa risposta del backend, ed e' cio'
    // che distingue "filtra per stati" da "filtra per tipo".
    if (kanbanId > 0) {
      const { kanban, stateIds } = kanbanStateIds(kanbanId);

      // Il filtro salvato RESTRINGE dentro gli stati, non li sostituisce: stesso ordine del
      // backend, dove il criterio del filtro si aggiunge alla query gia' ristretta alla pipeline.
      result = result.filter(
        (t) => stateIds.has(t.currentStateId) && matchesTaskKanbanFilter(t, kanban?.filter));
    }

    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (t) => t.title.toLowerCase().includes(needle) ||
          (t.notes?.toLowerCase().includes(needle) ?? false),
      );
    }
    if (kanbanId <= 0) {
      // Le date come nel backend (FILO-41), insieme alla ricerca; kanbanId le ignora.
      const from = url.searchParams.get('expirationDateFrom');
      const to = url.searchParams.get('expirationDateTo');
      result = result.filter((t) => inMockDateRange(t.expirationDate, from, to));
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, taskToDto));
  }),
  http.post(`${API}/tasks/:groupId`, async ({ request }) => {
    const input = (await request.json()) as TaskInput;
    const patch = taskInputFromDto(input);
    const now = new Date().toISOString();
    const created: Task = {
      taskId: nextId++,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: mockLoginUser.fullName,
      createDate: now, modifiedDate: now,
      title: patch.title ?? '',
      type: patch.type ?? 'activity',
      priority: patch.priority ?? 'none',
      status: 'todo',
      expirationDate: patch.expirationDate ?? null,
      remindDate: patch.remindDate ?? null,
      recurrent: patch.recurrent ?? false,
      recurrentValue: patch.recurrentValue ?? 0,
      recurrentUnit: patch.recurrentUnit ?? 'none',
      assignedUserId: patch.assignedUserId ?? 0,
      queueId: patch.queueId ?? null,
      notes: patch.notes ?? null,
      // Un task nasce senza collocazione: il funnel e' un asse in piu', non un requisito.
      currentStateId: 0,
    };
    mockTasks.push(created);
    return HttpResponse.json(taskToDto(created), { status: 201 });
  }),
  http.get(`${API}/task/:id`, ({ params }) => {
    const item = visibleTask(Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(taskToDto(item));
  }),
  // Endpoint dedicato per la sola transizione di stato.
  // Deve precedere `PATCH /task/:id` perche' MSW matcha in ordine di
  // registrazione e `:id` prenderebbe anche "12/status".
  http.patch(`${API}/task/:id/status`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = visibleTaskIndex(id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as TaskStatusInput;
    const patch = taskStatusInputFromDto(input);
    mockTasks[idx] = { ...mockTasks[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(taskToDto(mockTasks[idx]));
  }),
  http.patch(`${API}/task/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = visibleTaskIndex(id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as TaskInput;
    const patch = taskInputFromDto(input);
    mockTasks[idx] = { ...mockTasks[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(taskToDto(mockTasks[idx]));
  }),
  http.delete(`${API}/task/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = visibleTaskIndex(id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockTasks.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // NOTE — allineato a NotesResourceImpl.java (commit `090a0e38`)
  //   GET    /notes/{groupId}?search=&sort=&page=&pageSize=  -> Page<NoteDto>
  //   POST   /notes/{groupId}  NoteInput                     -> NoteDto
  //   GET    /note/{noteId}                                   -> NoteDto
  //   PATCH  /note/{noteId}   NoteInput                       -> NoteDto
  //   DELETE /note/{noteId}                                   -> 200
  // Filtri sort/pageSize/search applicati client-side coerenti con Task.
  // ============================================================
  http.get(`${API}/notes/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockNotes;
    if (search) {
      const q = search.toLowerCase();
      result = mockNotes.filter(
        (n) => n.title.toLowerCase().includes(q) || n.description.toLowerCase().includes(q),
      );
    }
    return HttpResponse.json(buildPage(result, page, pageSize, noteToDto));
  }),
  http.post(`${API}/notes/:groupId`, async ({ request }) => {
    const input = (await request.json()) as NoteInput;
    const patch = noteInputFromDto(input);
    const created: Note = {
      noteId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      title: patch.title ?? '',
      description: patch.description ?? '',
    };
    mockNotes.push(created);
    return HttpResponse.json(noteToDto(created), { status: 201 });
  }),
  http.get(`${API}/note/:id`, ({ params }) => {
    const item = mockNotes.find((n) => n.noteId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(noteToDto(item));
  }),
  http.patch(`${API}/note/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockNotes.findIndex((n) => n.noteId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as NoteInput;
    const patch = noteInputFromDto(input);
    mockNotes[idx] = { ...mockNotes[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(noteToDto(mockNotes[idx]));
  }),
  http.delete(`${API}/note/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockNotes.findIndex((n) => n.noteId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockNotes.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // LINKEDIN POST — allineato a LinkedinPostsResourceImpl.java
  //   GET    /linkedin-posts/{groupId}?search=&sort=&page=&pageSize=
  //   POST   /linkedin-posts/{groupId}  LinkedinPostInput
  //   GET    /linkedin-post/{linkedinPostId}
  //   PATCH  /linkedin-post/{linkedinPostId}
  //   DELETE /linkedin-post/{linkedinPostId}
  // ============================================================
  http.get(`${API}/linkedin-posts/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockLinkedinPosts;
    if (search) {
      const q = search.toLowerCase();
      result = mockLinkedinPosts.filter((l) => l.message.toLowerCase().includes(q));
    }
    return HttpResponse.json(buildPage(result, page, pageSize, linkedinPostToDto));
  }),
  http.post(`${API}/linkedin-posts/:groupId`, async ({ request }) => {
    const input = (await request.json()) as LinkedinPostInput;
    const patch = linkedinPostInputFromDto(input);
    const created: LinkedinPost = {
      linkedinPostId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      activityDate: patch.activityDate ?? new Date().toISOString(),
      message: patch.message ?? '',
    };
    mockLinkedinPosts.push(created);
    return HttpResponse.json(linkedinPostToDto(created), { status: 201 });
  }),
  http.get(`${API}/linkedin-post/:id`, ({ params }) => {
    const item = mockLinkedinPosts.find((l) => l.linkedinPostId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(linkedinPostToDto(item));
  }),
  http.patch(`${API}/linkedin-post/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockLinkedinPosts.findIndex((l) => l.linkedinPostId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as LinkedinPostInput;
    const patch = linkedinPostInputFromDto(input);
    mockLinkedinPosts[idx] = {
      ...mockLinkedinPosts[idx],
      ...patch,
      modifiedDate: new Date().toISOString(),
    };
    return HttpResponse.json(linkedinPostToDto(mockLinkedinPosts[idx]));
  }),
  http.delete(`${API}/linkedin-post/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockLinkedinPosts.findIndex((l) => l.linkedinPostId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockLinkedinPosts.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // CALL — allineato a CallsResourceImpl.java (commit `090a0e38`)
  //   GET    /calls/{groupId}/{direction}?status=&search=&page=&pageSize= -> Page<CallDto>
  //   POST   /calls/{groupId}  CallInput                                   -> CallDto
  //   GET    /call/{callId}                                                 -> CallDto
  //   PATCH  /call/{callId}   CallInput                                    -> CallDto
  //   DELETE /call/{callId}                                                 -> 200
  // direction nel PATH come Messages; filtri status/search query-opt.
  // ============================================================
  http.get(`${API}/calls/:groupId/:direction`, ({ request, params }) => {
    const url = new URL(request.url);
    const direction = String(params.direction) as CallDirection;
    const search = url.searchParams.get('search');
    const statusParam = url.searchParams.get('status');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockCalls.filter((c) => c.direction === direction);
    if (statusParam) {
      result = result.filter((c) => c.status === statusParam);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) => c.description.toLowerCase().includes(q));
    }
    return HttpResponse.json(buildPage(result, page, pageSize, callToDto));
  }),
  http.post(`${API}/calls/:groupId`, async ({ request }) => {
    const input = (await request.json()) as CallInput;
    const patch = callInputFromDto(input);
    const created: Call = {
      callId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      direction: patch.direction ?? 'OUT',
      callDate: patch.callDate ?? new Date().toISOString(),
      description: patch.description ?? '',
      actionItems: patch.actionItems,
      status: patch.status ?? 'connected',
    };
    mockCalls.push(created);
    return HttpResponse.json(callToDto(created), { status: 201 });
  }),
  http.get(`${API}/call/:id`, ({ params }) => {
    const item = mockCalls.find((c) => c.callId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(callToDto(item));
  }),
  http.patch(`${API}/call/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockCalls.findIndex((c) => c.callId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as CallInput;
    const patch = callInputFromDto(input);
    mockCalls[idx] = { ...mockCalls[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(callToDto(mockCalls[idx]));
  }),
  http.delete(`${API}/call/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockCalls.findIndex((c) => c.callId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockCalls.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // EMAIL — allineato a EmailsResourceImpl.java
  //   GET    /emails/{groupId}/{direction}?search=&page=&pageSize= -> Page<EmailDto>
  //   POST   /emails/{groupId}  EmailInput                          -> EmailDto
  //   GET    /email/{emailId}                                        -> EmailDto
  //   PATCH  /email/{emailId}   EmailInput                           -> EmailDto
  //   DELETE /email/{emailId}                                        -> 200
  // Stesso pattern di Call/Messages: direction nel PATH. Niente status.
  // ============================================================
  http.get(`${API}/emails/:groupId/:direction`, ({ request, params }) => {
    const url = new URL(request.url);
    const direction = String(params.direction) as EmailDirection;
    const search = url.searchParams.get('search');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockEmails.filter((e) => e.direction === direction);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((e) => e.description.toLowerCase().includes(q));
    }
    return HttpResponse.json(buildPage(result, page, pageSize, emailToDto));
  }),
  http.post(`${API}/emails/:groupId`, async ({ request }) => {
    const input = (await request.json()) as EmailInput;
    const patch = emailInputFromDto(input);
    const created: Email = {
      emailId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      direction: patch.direction ?? 'OUT',
      emailDate: patch.emailDate ?? new Date().toISOString(),
      description: patch.description ?? '',
    };
    mockEmails.push(created);
    return HttpResponse.json(emailToDto(created), { status: 201 });
  }),
  http.get(`${API}/email/:id`, ({ params }) => {
    const item = mockEmails.find((e) => e.emailId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(emailToDto(item));
  }),
  http.patch(`${API}/email/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockEmails.findIndex((e) => e.emailId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as EmailInput;
    const patch = emailInputFromDto(input);
    mockEmails[idx] = { ...mockEmails[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(emailToDto(mockEmails[idx]));
  }),
  http.delete(`${API}/email/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockEmails.findIndex((e) => e.emailId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockEmails.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // MEETING — allineato a MeetingsResourceImpl.java (commit `090a0e38`)
  //   GET    /meetings/{groupId}?status=&search=&page=&pageSize= -> Page<MeetingDto>
  //   POST   /meetings/{groupId}  MeetingInput                    -> MeetingDto
  //   GET    /meeting/{meetingId}                                  -> MeetingDto
  //   PATCH  /meeting/{meetingId} MeetingInput                    -> MeetingDto
  //   DELETE /meeting/{meetingId}                                  -> 200
  // Niente direction nel path (a differenza di Call); status filtra query.
  // ============================================================
  http.get(`${API}/meetings/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const statusParam = url.searchParams.get('status');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockMeetings;
    if (statusParam) {
      result = result.filter((m) => m.status === statusParam);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((m) => m.description.toLowerCase().includes(q));
    }
    return HttpResponse.json(buildPage(result, page, pageSize, meetingToDto));
  }),
  http.post(`${API}/meetings/:groupId`, async ({ request }) => {
    const input = (await request.json()) as MeetingInput;
    const patch = meetingInputFromDto(input);
    const created: Meeting = {
      meetingId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      meetingDate: patch.meetingDate ?? new Date().toISOString(),
      duration: patch.duration ?? 30,
      description: patch.description ?? '',
      status: patch.status ?? 'planned',
    };
    mockMeetings.push(created);
    return HttpResponse.json(meetingToDto(created), { status: 201 });
  }),
  http.get(`${API}/meeting/:id`, ({ params }) => {
    const item = mockMeetings.find((m) => m.meetingId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(meetingToDto(item));
  }),
  http.patch(`${API}/meeting/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockMeetings.findIndex((m) => m.meetingId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as MeetingInput;
    const patch = meetingInputFromDto(input);
    mockMeetings[idx] = { ...mockMeetings[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(meetingToDto(mockMeetings[idx]));
  }),
  http.delete(`${API}/meeting/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockMeetings.findIndex((m) => m.meetingId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockMeetings.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // COMMENT — allineato a CommentsResourceImpl.java (entità polimorfica)
  //   GET    /comments/{groupId}?className=<fqn>&classPK=<id> -> Page<Comment>
  //   POST   /comments/{groupId}  CommentInput                -> Comment
  //   PATCH  /comment/{commentId} { body }                    -> Comment
  //   DELETE /comment/{commentId}                             -> 200
  // className nel query è il FQN Java dell'entità target; filtro per (className, classPK).
  // ============================================================
  http.get(`${API}/comments/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const className = url.searchParams.get('className');
    const classPK = Number(url.searchParams.get('classPK'));
    const items = mockComments.filter(
      (c) => c.className === className && c.classPK === classPK,
    );
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),
  http.post(`${API}/comments/:groupId`, async ({ request }) => {
    const payload = (await request.json()) as CommentCreatePayload;
    const created: Comment = {
      commentId: nextId++,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Jader Francia',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
      className: payload.className,
      classPK: payload.classPK,
      body: payload.body,
    };
    mockComments.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/comment/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockComments.findIndex((c) => c.commentId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const patch = (await request.json()) as { body: string };
    mockComments[idx] = {
      ...mockComments[idx],
      body: patch.body,
      modifiedDate: new Date().toISOString(),
    };
    return HttpResponse.json(mockComments[idx]);
  }),
  http.delete(`${API}/comment/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockComments.findIndex((c) => c.commentId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockComments.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // BUSINESS — allineato a BusinessesResourceImpl.java
  //   GET    /businesses/{groupId}?search=&sort=&page=&pageSize= -> Page<BusinessDto>
  //   GET    /businesses/{groupId}/resolve?name=&domain=&emailAddress= -> BusinessDto | 404
  //   POST   /businesses/{groupId}  BusinessInput                 -> BusinessDto
  //   GET    /business/{businessId}                                -> BusinessDto
  //   PATCH  /business/{businessId} BusinessInput                  -> BusinessDto
  //   DELETE /business/{businessId}                                -> 200
  // ============================================================
  http.get(`${API}/businesses/:groupId/resolve`, ({ request }) => {
    const url = new URL(request.url);
    const found = resolveMockBusiness(
      url.searchParams.get('name'),
      url.searchParams.get('domain'),
      url.searchParams.get('emailAddress'),
    );
    // 404 = "nessuna corrispondenza", non un errore: e' la risposta prevista dal contratto.
    if (!found) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(businessToDto(found));
  }),
  http.get(`${API}/businesses/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    const kanbanId = Number(url.searchParams.get('kanbanId') ?? 0);

    let result = mockBusinesses;

    // kanbanId = le aziende della bacheca, cioe' quelle collocate in uno degli stati della sua
    // pipeline. Ha precedenza sugli altri filtri, come lato server.
    if (kanbanId > 0) {
      const { kanban, stateIds } = kanbanStateIds(kanbanId);

      result = result.filter(
        (b) => stateIds.has(b.currentStateId) && matchesBusinessKanbanFilter(b, kanban?.filter),
      );
    }

    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (b) => b.name?.toLowerCase().includes(needle) ||
          b.domain?.toLowerCase().includes(needle) ||
          (b.owner?.toLowerCase().includes(needle) ?? false),
      );
    }

    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, businessToDto));
  }),
  http.post(`${API}/businesses/:groupId`, async ({ request }) => {
    const input = (await request.json()) as BusinessInput;
    const patch = businessInputFromDto(input);
    const now = new Date().toISOString();
    const created: Business = {
      businessId: nextId++,
      externalCode: patch.externalCode ?? null,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
      createDate: now, modifiedDate: now,
      name: patch.name ?? '',
      domain: patch.domain ?? '',
      owner: patch.owner ?? null,
      sector: patch.sector ?? null,
      type: patch.type ?? null,
      employees: patch.employees ?? null,
      status: 'attivo',
      portraitURL: null,
      emailAddresses: [],
      phones: [],
      addresses: [],
      // Un'azienda nasce fuori da ogni bacheca: la collocazione la scrive il drag&drop, o la PATCH
      // con currentStateId. Come lato server, dove addBusiness non la conosce.
      currentStateId: patch.currentStateId ?? 0,
    };
    mockBusinesses.push(created);
    return HttpResponse.json(businessToDto(created), { status: 201 });
  }),
  http.get(`${API}/business/:id`, ({ params }) => {
    const item = mockBusinesses.find((b) => b.businessId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(businessToDto(item));
  }),
  http.patch(`${API}/business/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockBusinesses.findIndex((b) => b.businessId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as BusinessInput;
    const patch = businessInputFromDto(input);
    mockBusinesses[idx] = { ...mockBusinesses[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(businessToDto(mockBusinesses[idx]));
  }),
  http.delete(`${API}/business/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockBusinesses.findIndex((b) => b.businessId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockBusinesses.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // PERSON — allineato a PeopleResourceImpl.java
  //   GET    /people/{groupId}/sources    -> Page<PersonSourceDto>  (read-only autocomplete)
  //   GET    /people/{groupId}?q=         -> Page<PersonDto>
  //   POST   /people/{groupId}  PersonInput -> PersonDto
  //   GET    /person/{personId}            -> PersonDto
  //   PATCH  /person/{personId} PersonInput -> PersonDto
  //   DELETE /person/{personId}            -> 200
  // ============================================================
  // NB: la rotta piu' specifica `/people/:groupId/sources` va PRIMA di
  // `/people/:groupId` per chiarezza, anche se MSW matcha sul numero di
  // segmenti e l'ordine non sarebbe stretto requisito.
  http.get(`${API}/people/:groupId/sources`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockPersonSources;
    if (search) {
      const needle = search.toLowerCase();
      result = mockPersonSources.filter((s) => s.name.toLowerCase().includes(needle));
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, (x) => x));
  }),
  http.get(`${API}/people/:groupId/channels`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockPersonChannels;
    if (search) {
      const needle = search.toLowerCase();
      result = mockPersonChannels.filter((c) => c.name.toLowerCase().includes(needle));
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, (x) => x));
  }),
  // GET /people/{groupId}/by-email-address?emailAddress= -> PersonDto | 204
  // Lookup anti-duplicato per email esatta (allineato a PeopleResourceImpl):
  // 200 col contatto se una Person usa quell'email, altrimenti 204 No Content.
  // Rotta piu' specifica: va PRIMA di /people/:groupId.
  http.get(`${API}/people/:groupId/by-email-address`, ({ request }) => {
    const url = new URL(request.url);
    const email = (url.searchParams.get('emailAddress') ?? '').trim().toLowerCase();
    if (!email) return new HttpResponse(null, { status: 204 });
    const person = mockPersons.find(
      (p) => p.emailAddresses?.some((e) => e.address?.toLowerCase() === email) ?? false,
    );
    return person
      ? HttpResponse.json(personToDto(person))
      : new HttpResponse(null, { status: 204 });
  }),
  http.get(`${API}/people/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    const kanbanId = Number(url.searchParams.get('kanbanId') ?? 0);

    let result = mockPersons;

    // kanbanId = i contatti della bacheca, cioe' quelli collocati in uno degli stati della sua
    // pipeline. Ha precedenza sugli altri filtri, come lato server.
    if (kanbanId > 0) {
      const { kanban, stateIds } = kanbanStateIds(kanbanId);

      result = result.filter(
        (p) => stateIds.has(p.currentStateId) && matchesPersonKanbanFilter(p, kanban?.filter),
      );
    }

    // Filtra su firstName/lastName/prima email.
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (p) => p.firstName?.toLowerCase().includes(needle) ||
          p.lastName?.toLowerCase().includes(needle) ||
          (p.emailAddresses?.[0]?.address?.toLowerCase().includes(needle) ?? false),
      );
    }

    // Ordina in base al sortParam (sintassi Liferay: "field:asc,field:desc").
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, personToDto));
  }),
  http.post(`${API}/people/:groupId`, async ({ request }) => {
    const input = (await request.json()) as PersonInput;
    const patch = personInputFromDto(input);
    resolvePersonSourcePatch(patch);
    resolvePersonChannelPatch(patch);
    const now = new Date().toISOString();
    const created: Person = {
      personId: nextId++,
      externalCode: patch.externalCode ?? null,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
      createDate: now, modifiedDate: now,
      firstName: patch.firstName ?? '',
      lastName: patch.lastName ?? '',
      location: patch.location ?? null,
      personSourceId: patch.personSourceId ?? 0,
      personSource: patch.personSource ?? '',
      personChannelId: patch.personChannelId ?? 0,
      personChannel: patch.personChannel ?? '',
      lastInteractionDate: now,
      lastCampaignDate: null,
      leadStatus: patch.leadStatus ?? 'nuovo',
      marketingStatus: patch.marketingStatus ?? 'lead',
      status: 'attivo',
      emailAddresses: [],
      phones: [],
      // Un contatto nasce fuori da ogni bacheca: la collocazione la scrive il drag&drop, o la PATCH
      // con currentStateId. Come lato server, dove addPerson non la conosce.
      currentStateId: patch.currentStateId ?? 0,
    };
    mockPersons.push(created);
    return HttpResponse.json(personToDto(created), { status: 201 });
  }),
  http.get(`${API}/person/:id`, ({ params }) => {
    const item = mockPersons.find((p) => p.personId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(personToDto(item));
  }),
  http.patch(`${API}/person/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockPersons.findIndex((p) => p.personId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as PersonInput;
    const patch = personInputFromDto(input);
    resolvePersonSourcePatch(patch);
    resolvePersonChannelPatch(patch);
    mockPersons[idx] = { ...mockPersons[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(personToDto(mockPersons[idx]));
  }),
  http.delete(`${API}/person/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockPersons.findIndex((p) => p.personId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockPersons.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // RAPID CONTACT — allineato al contratto rest-openapi.yaml
  //   GET    /rapid-contacts/{groupId}  (list, search, sort, pagination)
  //   POST   /rapid-contacts/{groupId}  RapidContactInput -> RapidContact (201)
  //   GET    /rapid-contact/{id}        -> RapidContact (404 se assente)
  //   PATCH  /rapid-contact/{id}        RapidContactInput parziale -> RapidContact
  //   DELETE /rapid-contact/{id}        -> 200 (404 se assente)
  // Search: filtra (case-insensitive) su firstName, lastName, email, companyName.
  // ============================================================
  http.get(`${API}/rapid-contacts/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    // imported=true -> vista importati (admin, read-only); default -> da importare.
    const wantImported = url.searchParams.get('imported') === 'true';

    // Filtro per stato di import (come il filtro backend imported.eq(...)).
    let result = mockRapidContacts.filter((r) => (r.imported ?? false) === wantImported);
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (r) => r.firstName.toLowerCase().includes(needle) ||
          r.lastName.toLowerCase().includes(needle) ||
          (r.email?.toLowerCase().includes(needle) ?? false) ||
          (r.companyName?.toLowerCase().includes(needle) ?? false),
      );
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(buildPage(result, page, pageSize, rapidContactToDto));
  }),
  // Import massivo (TS-100): flippa imported=true sui non ancora importati. NB: a differenza
  // del backend reale, il mock NON crea/collega alcun Person (nessun personId assegnato),
  // quindi gli importati NON compariranno in alcun pannello "Fiere". L'alert nativo qui
  // sotto lo evidenzia: vivendo SOLO nel mock handler non può mai comparire in produzione,
  // e lo stile "scarno" del browser segnala che non è una UI di prodotto.
  http.post(`${API}/rapid-contacts/:groupId/import`, () => {
    const toImport = mockRapidContacts.filter((r) => !r.imported);
    toImport.forEach((r) => { r.imported = true; });
    window.alert(
      '[MOCK] Import simulato.\n\n' +
      'In modalità mock i contatti NON vengono realmente collegati a un contatto di sistema ' +
      '(nessun Person creato/associato): compariranno nella vista "Importati" ma NON nei ' +
      'pannelli "Fiere" delle schede.\n\n' +
      'In produzione l\'import crea/collega i Person e popola i pannelli Fiere.',
    );
    return HttpResponse.json({ imported: toImport.length, tasks: 0, failed: [] });
  }),
  // by-person: contatti fiera importati e collegati al Person (pannello Fiere).
  http.get(`${API}/rapid-contacts/by-person/:personId`, ({ params }) => {
    const personId = Number(params.personId);
    const linked = mockRapidContacts.filter((r) => r.personId === personId);
    return HttpResponse.json(buildPage(linked, 1, Math.max(linked.length, 1), rapidContactToDto));
  }),
  http.post(`${API}/rapid-contacts/:groupId`, async ({ request }) => {
    const input = (await request.json()) as RapidContactInput;
    const patch = rapidContactInputFromDto(input);
    const now = new Date().toISOString();
    const created: RapidContact = {
      rapidContactId: nextId++,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
      createDate: now, modifiedDate: now,
      firstName: patch.firstName ?? '',
      lastName: patch.lastName ?? '',
      email: patch.email ?? null,
      phone: patch.phone ?? null,
      role: patch.role ?? null,
      companyName: patch.companyName ?? null,
      companyDomain: patch.companyDomain ?? null,
      companyAddress: patch.companyAddress ?? null,
      companyCountry: patch.companyCountry ?? null,
      fairEvent: patch.fairEvent ?? null,
      interest: patch.interest ?? null,
      productCategory: patch.productCategory ?? null,
      interestLevel: patch.interestLevel ?? 0,
      followupAction: patch.followupAction ?? null,
      followupStatusListTypeEntryKey: patch.followupStatusListTypeEntryKey ?? null,
      followupStatusName: patch.followupStatusListTypeEntryKey ?? null,
      notes: patch.notes ?? null,
      salespersonUserId: patch.salespersonUserId ?? 1,
      salespersonUserName: 'Sistema',
      imported: false, // nasce da importare (come il default backend imported=0).
    };
    mockRapidContacts.push(created);
    return HttpResponse.json(rapidContactToDto(created), { status: 201 });
  }),
  http.get(`${API}/rapid-contact/:id`, ({ params }) => {
    const item = mockRapidContacts.find((r) => r.rapidContactId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(rapidContactToDto(item));
  }),
  http.patch(`${API}/rapid-contact/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockRapidContacts.findIndex((r) => r.rapidContactId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as RapidContactInput;
    const patch = rapidContactInputFromDto(input);
    mockRapidContacts[idx] = {
      ...mockRapidContacts[idx],
      ...patch,
      modifiedDate: new Date().toISOString(),
    };
    return HttpResponse.json(rapidContactToDto(mockRapidContacts[idx]));
  }),
  http.delete(`${API}/rapid-contact/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockRapidContacts.findIndex((r) => r.rapidContactId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockRapidContacts.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // RELATIONSHIP — allineato al contratto Liferay
  //   GET    /relationships/{groupId}?className=&classPK=  -> {direct, inverse}
  //   GET    /relationships/{groupId}/by-classes?className=&otherClassNames= -> {items} (FILO-41)
  //   POST   /relationships/{groupId}                      -> Relationship (409 su duplicato)
  //   PATCH  /relationship/{id}                            -> Relationship (description e primary)
  //   DELETE /relationship/{id}  (singolare)               -> 200 (404 se assente)
  //
  // TS-15 invariante "al piu' 1 primary" per coppia (className1, classPK1, className2):
  // - POST: nuovi record nascono con primary=false (no auto-set).
  // - PATCH con primary=true: demote-others sui sibling della coppia + set true.
  // - PATCH con primary=false: clear, no side-effect sugli altri.
  // - DELETE: splice semplice, no auto-promote.
  // ============================================================
  http.get(`${API}/relationships/:groupId`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className');
    const classPK = p.get('classPK');
    if (!className || !classPK) {
      return HttpResponse.json({ title: 'className e classPK obbligatori' }, { status: 400 });
    }
    const pk = Number(classPK);
    const direct = mockRelationships.filter((r) => r.className1 === className && r.classPK1 === pk);
    const inverse = mockRelationships.filter((r) => r.className2 === className && r.classPK2 === pk);
    return HttpResponse.json({ direct, inverse });
  }),
  // FILO-41: i legami fra una classe e le classi dall'altro capo, nei due versi, in una chiamata. Come
  // filterByView nel backend, un legame verso un task che l'utente non vede resta fuori.
  http.get(`${API}/relationships/:groupId/by-classes`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className');
    const others = (p.get('otherClassNames') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!className || others.length === 0) {
      return HttpResponse.json({ title: 'className e otherClassNames obbligatori' }, { status: 400 });
    }
    const between = (a: string, b: string) => (a === className && others.includes(b)) || (b === className && others.includes(a));
    const taskSeen = (cls: string, pk: number) => cls !== ACTIVITY_FQN.TASK || !!visibleTask(pk);
    const items = mockRelationships.filter(
      (r) => between(r.className1, r.className2) && taskSeen(r.className1, r.classPK1) && taskSeen(r.className2, r.classPK2),
    );
    return HttpResponse.json({ items });
  }),
  // TS-59: bulk attività per entità. Aggrega dai mock per-tipo tutte le attività
  // collegate all'entità (via mockRelationships, forward + reverse) in proiezione
  // leggera ActivitySummary. Replica RelationshipsResourceImpl.getEntityActivities:
  // risposta Page, ordinata per data desc. Sostituisce i 7 fan-out per-id.
  http.get(`${API}/relationships/:groupId/activities`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className');
    const classPK = p.get('classPK');
    if (!className || !classPK) {
      return HttpResponse.json({ title: 'className e classPK obbligatori' }, { status: 400 });
    }
    const pk = Number(classPK);

    // PK delle attività collegate raggruppati per FQN (l'entità può essere lato 1 o 2).
    const idsByFqn = new Map<string, Set<number>>();
    const link = (fqn: string, id: number) => {
      if (!idsByFqn.has(fqn)) idsByFqn.set(fqn, new Set());
      idsByFqn.get(fqn)!.add(id);
    };
    for (const r of mockRelationships) {
      if (r.className1 === className && r.classPK1 === pk) link(r.className2, r.classPK2);
      else if (r.className2 === className && r.classPK2 === pk) link(r.className1, r.classPK1);
    }
    const has = (fqn: string, id: number) => idsByFqn.get(fqn)?.has(id) ?? false;
    // Snippet: strip tag HTML + troncatura (replica lato mock di HtmlUtil.extractText).
    const snippet = (html: string | null | undefined) =>
      (html ?? '').replace(/<[^>]*>/g, '').slice(0, 80);

    const items: ActivitySummary[] = [];
    for (const t of mockTasks) {
      if (has(ACTIVITY_FQN.TASK, t.taskId)) {
        items.push({
          type: 'TASK', className: ACTIVITY_FQN.TASK, classPK: t.taskId, title: t.title,
          date: t.createDate, userName: t.userName, status: taskStatusToDto(t.status),
          expirationDate: t.expirationDate,
        });
      }
    }
    for (const n of mockNotes) {
      if (has(ACTIVITY_FQN.NOTE, n.noteId)) {
        items.push({
          type: 'NOTE', className: ACTIVITY_FQN.NOTE, classPK: n.noteId, title: n.title,
          date: n.createDate, userName: n.userName,
        });
      }
    }
    for (const c of mockCalls) {
      if (has(ACTIVITY_FQN.CALL, c.callId)) {
        items.push({
          type: 'CALL', className: ACTIVITY_FQN.CALL, classPK: c.callId,
          title: snippet(c.description), date: c.callDate, userName: c.userName,
        });
      }
    }
    for (const e of mockEmails) {
      if (has(ACTIVITY_FQN.EMAIL, e.emailId)) {
        items.push({
          type: 'EMAIL', className: ACTIVITY_FQN.EMAIL, classPK: e.emailId,
          title: snippet(e.description), date: e.emailDate, userName: e.userName,
        });
      }
    }
    for (const m of mockMeetings) {
      if (has(ACTIVITY_FQN.MEETING, m.meetingId)) {
        items.push({
          type: 'MEETING', className: ACTIVITY_FQN.MEETING, classPK: m.meetingId,
          title: snippet(m.description), date: m.meetingDate, userName: m.userName,
        });
      }
    }
    for (const l of mockLinkedinPosts) {
      if (has(ACTIVITY_FQN.LINKEDIN, l.linkedinPostId)) {
        items.push({
          type: 'LINKEDIN', className: ACTIVITY_FQN.LINKEDIN, classPK: l.linkedinPostId,
          title: snippet(l.message), date: l.activityDate, userName: l.userName,
        });
      }
    }
    for (const msg of mockMessages) {
      if (has(ACTIVITY_FQN.MESSAGE, msg.messageId)) {
        items.push({
          type: 'MESSAGE', className: ACTIVITY_FQN.MESSAGE, classPK: msg.messageId,
          title: msg.subject, date: msg.receiptDate,
          userName: msg.senderName || msg.senderEmailAddress, direction: msg.direction,
          // Allegati (TS-108): la proiezione bulk li porta, il corpo no. Come il
          // backend, che li legge col finder G_M mentre il corpo resta sulla
          // proiezione caricata all'espansione.
          attachments: msg.attachments ?? [],
        });
      }
    }

    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),
  http.post(`${API}/relationships/:groupId`, async ({ request }) => {
    const payload = (await request.json()) as {
      className1: string; classPK1: number;
      className2: string; classPK2: number;
      description?: string | null;
    };
    const duplicate = mockRelationships.some(
      (r) =>
        r.className1 === payload.className1 && r.classPK1 === Number(payload.classPK1) &&
        r.className2 === payload.className2 && r.classPK2 === Number(payload.classPK2)
    );
    if (duplicate) {
      return HttpResponse.json(
        { title: 'Relazione gia\' esistente', type: 'DuplicateEntry' },
        { status: 409 }
      );
    }
    const created = {
      ...payload,
      classPK1: Number(payload.classPK1),
      classPK2: Number(payload.classPK2),
      description: payload.description ?? null,
      // TS-15: nuovo record nasce con primary=false (no auto-set).
      primary: false,
      relationshipId: nextId++,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
      createDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mockRelationships.push(created as any);
    // Contratto Liferay (OpenAPI): POST ritorna 200, non 201.
    return HttpResponse.json(created, { status: 200 });
  }),
  http.patch(`${API}/relationship/:id`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockRelationships.findIndex((r) => r.relationshipId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const body = (await request.json()) as { description?: string | null; primary?: boolean };
    const current = mockRelationships[idx];

    // TS-15: se primary=true viene impostato, demota gli altri sibling della
    // coppia (className1, classPK1, className2) per riflettere l'invariante
    // "al piu' 1 primary" del backend. Se primary=false, nessun side-effect.
    if (body.primary === true) {
      for (const sibling of mockRelationships) {
        if (
          sibling.relationshipId !== id &&
          sibling.className1 === current.className1 &&
          sibling.classPK1 === current.classPK1 &&
          sibling.className2 === current.className2 &&
          sibling.primary
        ) {
          sibling.primary = false;
          sibling.modifiedDate = new Date().toISOString();
        }
      }
    }

    mockRelationships[idx] = {
      ...current,
      description: body.description !== undefined ? body.description : current.description,
      primary: body.primary !== undefined ? body.primary : current.primary,
      modifiedDate: new Date().toISOString(),
    };
    return HttpResponse.json(mockRelationships[idx]);
  }),
  http.delete(`${API}/relationship/:id`, ({ params }) => {
    const idx = mockRelationships.findIndex((r) => r.relationshipId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    // TS-15: nessun auto-promote del sibling piu' vecchio. Se il record cancellato
    // era primary, la coppia resta senza primary (lecito).
    mockRelationships.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // TS-22: POST /relationship/{id}/pin — pinna e azzera il pin degli altri
  // record della stessa SORGENTE (className1, classPK1): al piu' 1 pinnata
  // per sorgente. Il vincolo NON include className2 (a differenza di primary).
  http.post(`${API}/relationship/:id/pin`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockRelationships.findIndex((r) => r.relationshipId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const current = mockRelationships[idx];
    for (const sibling of mockRelationships) {
      if (
        sibling.relationshipId !== id &&
        sibling.className1 === current.className1 &&
        sibling.classPK1 === current.classPK1 &&
        sibling.pinned
      ) {
        sibling.pinned = false;
        sibling.modifiedDate = new Date().toISOString();
      }
    }
    mockRelationships[idx] = { ...current, pinned: true, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(mockRelationships[idx]);
  }),
  // TS-22: POST /relationship/{id}/unpin — toglie il pin, nessun side-effect.
  http.post(`${API}/relationship/:id/unpin`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockRelationships.findIndex((r) => r.relationshipId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockRelationships[idx] = {
      ...mockRelationships[idx],
      pinned: false,
      modifiedDate: new Date().toISOString(),
    };
    return HttpResponse.json(mockRelationships[idx]);
  }),

  // ============================================================
  // MESSAGE — allineato a MessagesResourceImpl.java
  //   GET    /messages/{groupId}?search=&sort=&page=&pageSize= -> Page<MessageDto>
  //   POST   /messages/{groupId}  MessageInput                  -> MessageDto
  //   GET    /message/{messageId}                                -> MessageDto
  //   PATCH  /message/{messageId}/label  MessageLabelInput       -> MessageDto
  //   DELETE /message/{messageId}                                -> 200
  //   PATCH  /message/{messageId}/info/{messageInfoId}           -> MessageInfoDto
  // `label` e' int32 server-side (enum MessageLabel, vedi messageMappers).
  // `infos` e' eager-load: il lookup su mockMessageInfos replica il join
  // fatto da DTOConverterUtil.toMessage lato backend.
  // ============================================================
  http.get(`${API}/messages/:groupId/:direction`, ({ request, params }) => {
    const url = new URL(request.url);
    const direction = String(params.direction);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    // Come MessagesResourceImpl.getMessages: una label valida filtra su quella sola, altrimenti
    // valgono le tre da lavorare (TO_PROCESS, IN_PROGRESS, ERROR).
    const label = Number(url.searchParams.get('label') ?? -1);
    const labels = MESSAGE_LABELS.some((l) => messageLabelToInt(l.value) === label)
      ? [label]
      : (['to_process', 'in_progress', 'error'] as const).map(messageLabelToInt);

    // Scope per direzione (replica il path param backend del Messages list).
    let result = mockMessages.filter((m) => m.direction === direction && labels.includes(messageLabelToInt(m.label)));
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (m) => m.subject.toLowerCase().includes(needle) ||
          m.senderName.toLowerCase().includes(needle) ||
          m.body.toLowerCase().includes(needle),
      );
    }
    result = applySortSpec(result, sort);

    return HttpResponse.json(
      buildPage(result, page, pageSize, (m) => messageToDto(enrichMessageInfos(m))),
    );
  }),
  http.post(`${API}/messages/:groupId`, async ({ request }) => {
    const input = (await request.json()) as MessageInput;
    const patch = messageInputFromDto(input);
    const now = new Date().toISOString();
    const created: Message = {
      messageId: nextId++,
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: 'Sistema',
      createDate: now, modifiedDate: now,
      direction: patch.direction ?? 'IN',
      subject: patch.subject ?? '',
      body: patch.body ?? '',
      senderName: patch.senderName ?? '',
      senderEmailAddress: patch.senderEmailAddress ?? '',
      receiptDate: patch.receiptDate ?? now,
      label: patch.label ?? 'new',
      infos: [],
    };
    mockMessages.push(created);
    return HttpResponse.json(messageToDto(enrichMessageInfos(created)), { status: 201 });
  }),
  http.get(`${API}/message/:id`, ({ params }) => {
    const item = mockMessages.find((m) => m.messageId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(messageToDto(enrichMessageInfos(item)));
  }),
  // Proiezione per l'accordion della timeline: i campi mostrati + il CORPO (che l'accordion rende
  // come HTML). Restano fuori infos, allegati e analisi AI: quelli vivono su GET /message/:id.
  http.get(`${API}/message/:id/summary`, ({ params }) => {
    const item = mockMessages.find((m) => m.messageId === Number(params.id));
    if (!item) return new HttpResponse(null, { status: 404 });
    const { messageId, subject, body, bodyHtml, direction, receiptDate, senderName, senderEmailAddress } = item;
    return HttpResponse.json({
      messageId,
      subject,
      body,
      bodyHtml,
      direction,
      receiptDate,
      senderName,
      senderEmailAddress,
    });
  }),
  http.patch(`${API}/message/:id/label`, async ({ request, params }) => {
    const id = Number(params.id);
    const idx = mockMessages.findIndex((m) => m.messageId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as MessageInput;
    const patch = messageInputFromDto(input);
    mockMessages[idx] = { ...mockMessages[idx], ...patch, modifiedDate: new Date().toISOString() };
    return HttpResponse.json(messageToDto(enrichMessageInfos(mockMessages[idx])));
  }),
  http.patch(`${API}/message/:messageId/info/:messageInfoId`, async ({ request, params }) => {
    const messageId = Number(params.messageId);
    const messageInfoId = Number(params.messageInfoId);
    // Solo le info degli extractor (mockMessageInfos -> extractorInfos) sono editabili: la lookup
    // e' intenzionalmente su mockMessageInfos e NON su mockPredefinedMessageInfos (Message.infos),
    // che sono read-only. Un id predefinito qui cade quindi nel 404 sottostante (atteso).
    const idx = mockMessageInfos.findIndex((mi) => mi.messageInfoId === messageInfoId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    // Coerenza path: l'info deve appartenere al messageId indicato (replica validazione backend).
    if (mockMessageInfos[idx].messageId !== messageId) return new HttpResponse(null, { status: 404 });
    const input = (await request.json()) as MessageInfoInput;
    const patch = messageInfoInputFromDto(input);
    const updated: MessageInfo = {
      ...mockMessageInfos[idx],
      ...patch,
      modifiedDate: new Date().toISOString(),
    };
    mockMessageInfos[idx] = updated;
    return HttpResponse.json(messageInfoToDto(updated));
  }),
  // Download allegato: GET .../content -> octet-stream. In mock mode restituiamo
  // un contenuto testuale placeholder (gli allegati mock non hanno byte reali);
  // la UI riceve comunque un Blob e ne forza il salvataggio col fileName originale.
  http.get(`${API}/message/:messageId/attachment/:messageAttachmentId/content`, ({ params }) => {
    const messageId = Number(params.messageId);
    const messageAttachmentId = Number(params.messageAttachmentId);
    const msg = mockMessages.find((m) => m.messageId === messageId);
    const att = msg?.attachments?.find((a) => a.messageAttachmentId === messageAttachmentId);
    if (!att) return new HttpResponse(null, { status: 404 });
    const content = `Mock content for ${att.fileName} (attachment ${messageAttachmentId} of message ${messageId}).`;
    return new HttpResponse(content, {
      status: 200,
      headers: {
        'Content-Type': att.mimeType || 'application/octet-stream',
        'Content-Disposition': `attachment; filename="${att.fileName}"`,
      },
    });
  }),
  http.delete(`${API}/message/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockMessages.findIndex((m) => m.messageId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockMessages.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // USER (mock-only: 4 utenti finti per lookup assignedUserId).
  // Il backend Liferay espone SOLO GET /users/{groupId} (lista
  // Member del sito) — il client (userApi.ts) cachea la lista e
  // simula in-memory getById/search. Quindi 1 solo handler basta.
  // ============================================================
  http.get(`${API}/users/:groupId`, () =>
    // Shape Page (come il backend reale e gli altri mock lista): userApi legge r.data.items.
    HttpResponse.json({ items: MOCK_USERS, totalCount: MOCK_USERS.length, lastPage: 1 })),

  // POST /users (upsertUser): crea l'utente, lo aggiunge alla lista mock e lo ritorna.
  http.post(`${API}/users`, async ({ request }) => {
    const body = (await request.json()) as {
      firstName?: string; lastName?: string; emailAddress?: string; screenName?: string; role?: string;
    };
    const member = {
      userId: Math.max(0, ...MOCK_USERS.map((u) => u.userId)) + 1,
      // FILO-75: il nome scelto, altrimenti generato dall'email come fa Liferay.
      screenName: (body.screenName || (body.emailAddress ?? '').split('@')[0]).toLowerCase(),
      firstName: body.firstName ?? '',
      lastName: body.lastName ?? '',
      emailAddress: body.emailAddress ?? '',
      portraitURL: null,
      role: body.role ?? 'USER',
    };
    MOCK_USERS.push(member);
    return HttpResponse.json(member);
  }),

  // ============================================================
  // PERSON EMAIL ADDRESS — nested per owner, allineato a
  // PeopleResourceImpl.addPersonEmailAddress/update/delete
  //   POST   /person/{personId}/emailAddress                  -> EmailAddress
  //   PATCH  /person/{personId}/emailAddress/{emailAddressId} -> EmailAddress
  //   DELETE /person/{personId}/emailAddress/{emailAddressId} -> 200
  // ============================================================
  http.post(`${API}/person/:personId/emailAddress`, async ({ request, params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { address: string; primary: boolean };
    const created = { emailAddressId: nextId++, address: p.address, primary: p.primary };
    if (p.primary) {
      for (const email of parent.emailAddresses) email.primary = false;
    }
    parent.emailAddresses.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/person/:personId/emailAddress/:emailAddressId`, async ({ request, params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const emailAddressId = Number(params.emailAddressId);
    const idx = parent.emailAddresses.findIndex((e) => e.emailAddressId === emailAddressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { address?: string; primary?: boolean };
    if (p.primary === true) {
      for (const email of parent.emailAddresses) {
        if (email.emailAddressId !== emailAddressId) email.primary = false;
      }
    }
    parent.emailAddresses[idx] = {
      ...parent.emailAddresses[idx],
      ...(p.address !== undefined ? { address: p.address } : {}),
      ...(p.primary !== undefined ? { primary: p.primary } : {}),
    };
    return HttpResponse.json(parent.emailAddresses[idx]);
  }),
  http.delete(`${API}/person/:personId/emailAddress/:emailAddressId`, ({ params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const emailAddressId = Number(params.emailAddressId);
    const idx = parent.emailAddresses.findIndex((e) => e.emailAddressId === emailAddressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    parent.emailAddresses.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // PERSON PHONE — nested per owner, allineato a
  // PeopleResourceImpl.addPersonPhone/update/delete
  //   POST   /person/{personId}/phone             -> Phone
  //   PATCH  /person/{personId}/phone/{phoneId}   -> Phone
  //   DELETE /person/{personId}/phone/{phoneId}   -> 200
  // ============================================================
  http.post(`${API}/person/:personId/phone`, async ({ request, params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { number: string; primary: boolean };
    const created = { phoneId: nextId++, number: p.number, primary: p.primary };
    if (p.primary) {
      for (const phone of parent.phones) phone.primary = false;
    }
    parent.phones.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/person/:personId/phone/:phoneId`, async ({ request, params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const phoneId = Number(params.phoneId);
    const idx = parent.phones.findIndex((e) => e.phoneId === phoneId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { number?: string; primary?: boolean };
    if (p.primary === true) {
      for (const phone of parent.phones) {
        if (phone.phoneId !== phoneId) phone.primary = false;
      }
    }
    parent.phones[idx] = {
      ...parent.phones[idx],
      ...(p.number !== undefined ? { number: p.number } : {}),
      ...(p.primary !== undefined ? { primary: p.primary } : {}),
    };
    return HttpResponse.json(parent.phones[idx]);
  }),
  http.delete(`${API}/person/:personId/phone/:phoneId`, ({ params }) => {
    const parent = mockPersons.find((p) => p.personId === Number(params.personId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const phoneId = Number(params.phoneId);
    const idx = parent.phones.findIndex((e) => e.phoneId === phoneId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    parent.phones.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // BUSINESS EMAIL ADDRESS — nested per owner, allineato a
  // BusinessesResourceImpl.addBusinessEmailAddress/update/delete
  //   POST   /business/{businessId}/emailAddress                  -> EmailAddress
  //   PATCH  /business/{businessId}/emailAddress/{emailAddressId} -> EmailAddress
  //   DELETE /business/{businessId}/emailAddress/{emailAddressId} -> 200
  // ============================================================
  http.post(`${API}/business/:businessId/emailAddress`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { address: string; primary: boolean };
    const created = { emailAddressId: nextId++, address: p.address, primary: p.primary };
    if (p.primary) {
      for (const email of parent.emailAddresses) email.primary = false;
    }
    parent.emailAddresses.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/business/:businessId/emailAddress/:emailAddressId`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const emailAddressId = Number(params.emailAddressId);
    const idx = parent.emailAddresses.findIndex((e) => e.emailAddressId === emailAddressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { address?: string; primary?: boolean };
    if (p.primary === true) {
      for (const email of parent.emailAddresses) {
        if (email.emailAddressId !== emailAddressId) email.primary = false;
      }
    }
    parent.emailAddresses[idx] = {
      ...parent.emailAddresses[idx],
      ...(p.address !== undefined ? { address: p.address } : {}),
      ...(p.primary !== undefined ? { primary: p.primary } : {}),
    };
    return HttpResponse.json(parent.emailAddresses[idx]);
  }),
  http.delete(`${API}/business/:businessId/emailAddress/:emailAddressId`, ({ params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const emailAddressId = Number(params.emailAddressId);
    const idx = parent.emailAddresses.findIndex((e) => e.emailAddressId === emailAddressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    parent.emailAddresses.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // BUSINESS PHONE — nested per owner, allineato a
  // BusinessesResourceImpl.addBusinessPhone/update/delete
  //   POST   /business/{businessId}/phone             -> Phone
  //   PATCH  /business/{businessId}/phone/{phoneId}   -> Phone
  //   DELETE /business/{businessId}/phone/{phoneId}   -> 200
  // ============================================================
  http.post(`${API}/business/:businessId/phone`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { number: string; primary: boolean };
    const created = { phoneId: nextId++, number: p.number, primary: p.primary };
    if (p.primary) {
      for (const phone of parent.phones) phone.primary = false;
    }
    parent.phones.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/business/:businessId/phone/:phoneId`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const phoneId = Number(params.phoneId);
    const idx = parent.phones.findIndex((e) => e.phoneId === phoneId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as { number?: string; primary?: boolean };
    if (p.primary === true) {
      for (const phone of parent.phones) {
        if (phone.phoneId !== phoneId) phone.primary = false;
      }
    }
    parent.phones[idx] = {
      ...parent.phones[idx],
      ...(p.number !== undefined ? { number: p.number } : {}),
      ...(p.primary !== undefined ? { primary: p.primary } : {}),
    };
    return HttpResponse.json(parent.phones[idx]);
  }),
  http.delete(`${API}/business/:businessId/phone/:phoneId`, ({ params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const phoneId = Number(params.phoneId);
    const idx = parent.phones.findIndex((e) => e.phoneId === phoneId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    parent.phones.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // BUSINESS ADDRESS — nested per owner, allineato a
  // BusinessesResourceImpl.addBusinessAddress/update/delete.
  //   POST   /business/{businessId}/address              -> Address
  //   PATCH  /business/{businessId}/address/{addressId}  -> Address
  //   DELETE /business/{businessId}/address/{addressId}  -> 200
  //
  // Shape AddressInput accettato: { name?, description?, street?, city?,
  // zip?, primary?, countryId?, regionId? }. Il backend reale risolve
  // countryId -> country (codice ISO2) e regionId -> region (label) via
  // join con tabelle Liferay core; qui simuliamo lo stesso lookup in
  // mockCountries / mockRegionsByCountry per popolare le label
  // denormalizzate, cosi' la GET successiva ritorna lo stesso shape.
  // ============================================================
  http.post(`${API}/business/:businessId/address`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as {
      name?: string; description?: string;
      street?: string; city?: string; zip?: string; primary?: boolean;
      countryId?: number | null; regionId?: number | null;
    };
    if (!isPartialAddress(p)) return HttpResponse.json({ status: 'BAD_REQUEST', title: '' }, { status: 400 });
    const country = p.countryId != null ? mockCountries.find((c) => c.countryId === p.countryId) : undefined;
    const region = p.regionId != null && p.countryId != null
      ? (mockRegionsByCountry[p.countryId] ?? []).find((r) => r.regionId === p.regionId)
      : undefined;
    const created = {
      addressId: nextId++,
      ...(p.name !== undefined ? { name: p.name } : {}),
      ...(p.description !== undefined ? { description: p.description } : {}),
      street: p.street?.trim() || '-',
      city: p.city?.trim() || '-',
      ...(p.zip !== undefined ? { zip: p.zip } : {}),
      ...(p.countryId != null ? { countryId: p.countryId, country: country?.a2 ?? '' } : {}),
      ...(p.regionId != null ? { regionId: p.regionId, region: region?.title ?? '' } : {}),
      primary: !!p.primary,
    };
    if (p.primary) {
      for (const addr of parent.addresses) addr.primary = false;
    }
    parent.addresses.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),
  http.patch(`${API}/business/:businessId/address/:addressId`, async ({ request, params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const addressId = Number(params.addressId);
    const idx = parent.addresses.findIndex((a) => a.addressId === addressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const p = (await request.json()) as {
      name?: string; description?: string;
      street?: string; city?: string; zip?: string; primary?: boolean;
      countryId?: number | null; regionId?: number | null;
    };
    if (p.primary === true) {
      for (const addr of parent.addresses) {
        if (addr.addressId !== addressId) addr.primary = false;
      }
    }
    // Denormalizza country/region label dal lookup id, replicando il
    // resolve server-side. countryId=null azzera entrambi (uscita dal
    // paese). regionId=null azzera solo la region (cambio paese senza
    // selezionare una nuova region).
    const current = parent.addresses[idx];
    const nextCountryId = p.countryId !== undefined ? p.countryId : current.countryId;
    const nextRegionId = p.regionId !== undefined ? p.regionId : current.regionId;
    const country = nextCountryId != null ? mockCountries.find((c) => c.countryId === nextCountryId) : undefined;
    const region = nextRegionId != null && nextCountryId != null
      ? (mockRegionsByCountry[nextCountryId] ?? []).find((r) => r.regionId === nextRegionId)
      : undefined;
    // I campi assenti restano quelli salvati, poi la stessa regola della creazione (AddressRules).
    const merged = {
      name: p.name ?? current.name, description: p.description ?? current.description,
      street: p.street ?? current.street, city: p.city ?? current.city, zip: p.zip ?? current.zip,
      regionId: nextRegionId,
    };
    if (!isPartialAddress(merged)) return HttpResponse.json({ status: 'BAD_REQUEST', title: '' }, { status: 400 });
    parent.addresses[idx] = {
      ...current,
      ...(p.name !== undefined ? { name: p.name } : {}),
      ...(p.description !== undefined ? { description: p.description } : {}),
      street: merged.street?.trim() || '-',
      city: merged.city?.trim() || '-',
      ...(p.zip !== undefined ? { zip: p.zip } : {}),
      ...(p.primary !== undefined ? { primary: p.primary } : {}),
      ...(p.countryId !== undefined
        ? p.countryId === null
          ? { countryId: undefined, country: '' }
          : { countryId: p.countryId, country: country?.a2 ?? '' }
        : {}),
      ...(p.regionId !== undefined
        ? p.regionId === null
          ? { regionId: undefined, region: '' }
          : { regionId: p.regionId, region: region?.title ?? '' }
        : {}),
    };
    return HttpResponse.json(parent.addresses[idx]);
  }),
  http.delete(`${API}/business/:businessId/address/:addressId`, ({ params }) => {
    const parent = mockBusinesses.find((b) => b.businessId === Number(params.businessId));
    if (!parent) return new HttpResponse(null, { status: 404 });
    const addressId = Number(params.addressId);
    const idx = parent.addresses.findIndex((a) => a.addressId === addressId);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    parent.addresses.splice(idx, 1);
    return new HttpResponse(null, { status: 200 });
  }),

  // ============================================================
  // GEOGRAPHY — country + region read-only, allineato a
  // CountriesResourceImpl / RegionsResourceImpl backend Liferay.
  //   GET /countries                       -> Page<Country>
  //   GET /country/{countryId}/regions     -> Page<Region>
  // I dati sono cherry-pickati in geographyMocks.ts (10 country dei
  // mercati core + ~10 region per country). Wrappiamo in Page<T> per
  // fedelta' al runtime Liferay (lo schema OpenAPI dichiara array, ma
  // REST Builder wrappa sempre).
  // ============================================================
  http.get(`${API}/countries`, () =>
    HttpResponse.json(buildPage(mockCountries, 1, Math.max(mockCountries.length, 1), (x) => x)),
  ),
  http.get(`${API}/country/:countryId/regions`, ({ params }) => {
    const items = mockRegionsByCountry[Number(params.countryId)] ?? [];
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),

  // ============================================================
  // WHATSAPP — allineato a WhatsAppMessagesResourceImpl.java
  //   GET  /whatsapp-messages/{groupId}              -> Page<WhatsAppMessage>
  //   GET  /whatsapp-message/{id}                    -> WhatsAppMessage
  //   POST /whatsapp-messages/{groupId}/send  Input  -> WhatsAppMessage
  //   GET  /whatsapp-sessions/{groupId}/{personId}   -> Page<WhatsAppSession> (annidato)
  // Le sessioni raggruppano i messaggi via whatsAppSessionId (mock-side);
  // startDate/lastMessageDate/messageCount/active sono derivati qui dai messaggi,
  // come il backend fa in DTOConverterUtil.
  // ============================================================
  http.get(`${API}/whatsapp-messages/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    const search = url.searchParams.get('search');

    let result = [...mockWhatsAppMessages];
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (m) =>
          (m.body ?? '').toLowerCase().includes(needle) ||
          (m.phoneNumber ?? '').includes(needle),
      );
    }
    result.sort((a, b) => (b.receiptDate ?? '').localeCompare(a.receiptDate ?? ''));

    return HttpResponse.json(buildPage(result, page, pageSize, whatsAppMessageToDto));
  }),

  http.get(`${API}/whatsapp-message/:id`, ({ params }) => {
    const id = Number(params.id);
    const found = mockWhatsAppMessages.find((m) => m.whatsAppMessageId === id);
    if (!found) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(whatsAppMessageToDto(found));
  }),

  http.post(`${API}/whatsapp-messages/:groupId/send`, async ({ request }) => {
    const body = (await request.json()) as {
      personId: number;
      phoneNumber: string;
      body: string;
      phoneNumberId?: string;
    };
    const nowMs = Date.now();
    const nowIso = new Date(nowMs).toISOString();
    const digits = phoneDigits(String(body.phoneNumber ?? ''));
    const personId = Number(body.personId);

    // Gate della finestra, come lato server: il testo libero è lecito solo se il
    // contatto ha scritto entro 24h. Il mock non ha un template di ricontatto
    // configurato, quindi qui si risponde sempre 409 e la UI apre il selettore.
    if (!mockFreeFormAllowed(digits, personId)) {
      return mockWindowConflict(digits, personId);
    }

    const session = attachOrCreateMockSession(digits, personId, nowMs, nowIso);

    const message = {
      whatsAppMessageId: nextId++,
      whatsAppSessionId: session.whatsAppSessionId,
      direction: 'OUT' as const,
      externalId: `wamid.mock-${nowMs}`,
      phoneNumber: digits,
      senderName: '',
      phoneNumberId: body.phoneNumberId ?? session.phoneNumberId,
      type: 'TEXT' as const,
      body: String(body.body ?? ''),
      receiptDate: nowIso,
      status: 'ACCEPTED' as const,
      statusDate: nowIso,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: nowIso,
      modifiedDate: nowIso,
    };
    mockWhatsAppMessages.push(message);
    return HttpResponse.json(whatsAppMessageToDto(message));
  }),

  // Template approvati da Meta: gli unici messaggi consentiti a finestra chiusa.
  http.get(`${API}/whatsapp-templates/:groupId`, () =>
    HttpResponse.json(
      buildPage(mockWhatsAppTemplates, 1, Math.max(mockWhatsAppTemplates.length, 1), (x) => x),
    ),
  ),

  // Numeri mittente della company: popolano la combo di scelta del mittente.
  http.get(`${API}/whatsapp-numbers/:groupId`, () =>
    HttpResponse.json(
      buildPage(mockWhatsAppNumbers, 1, Math.max(mockWhatsAppNumbers.length, 1), (x) => x),
    ),
  ),

  // Invio di un template: persiste il testo RESO (parametri sostituiti), cioe' quello
  // che il contatto riceve davvero. Non riapre la finestra: la sessione resta senza
  // messaggi in ingresso, quindi PENDING.
  http.post(
    `${API}/whatsapp-messages/:groupId/send-template`,
    async ({ request }) => {
      const body = (await request.json()) as {
        personId: number;
        phoneNumber: string;
        templateName: string;
        parameters?: string[];
      };
      const nowMs = Date.now();
      const nowIso = new Date(nowMs).toISOString();
      const digits = phoneDigits(String(body.phoneNumber ?? ''));

      const template = mockWhatsAppTemplates.find(
        (tpl) => tpl.name === body.templateName,
      );
      if (!template) return new HttpResponse(null, { status: 404 });

      // replaceAll come il server (String.replace di Java sostituisce tutte le occorrenze):
      // con `replace` il mock riproduceva lo stesso errore del picker, e quindi lo nascondeva.
      const rendered = (body.parameters ?? []).reduce(
        (text, value, i) => text.replaceAll(`{{${i + 1}}}`, value ?? ''),
        template.body,
      );

      const session = attachOrCreateMockSession(
        digits,
        Number(body.personId),
        nowMs,
        nowIso,
      );

      const message = {
        whatsAppMessageId: nextId++,
        whatsAppSessionId: session.whatsAppSessionId,
        direction: 'OUT' as const,
        externalId: `wamid.mock-${nowMs}`,
        phoneNumber: digits,
        senderName: '',
        phoneNumberId: session.phoneNumberId,
        type: 'TEMPLATE' as const,
        body: rendered,
        receiptDate: nowIso,
        status: 'ACCEPTED' as const,
        statusDate: nowIso,
        companyId: 1,
        groupId: MOCK_GROUP_ID,
        userId: 1,
        userName: 'Sistema',
        createDate: nowIso,
        modifiedDate: nowIso,
      };
      mockWhatsAppMessages.push(message);
      return HttpResponse.json(whatsAppMessageToDto(message));
    },
  ),

  // POST multipart send-media: crea un messaggio media OUT dal file caricato e ne
  // memorizza il blob (servito da /media/content). Deriva il tipo dal mimeType.
  http.post(`${API}/whatsapp-messages/:groupId/send-media`, async ({ request }) => {
    const form = await request.formData();
    const personId = Number(form.get('personId'));
    const phoneNumber = String(form.get('phoneNumber') ?? '');
    const caption = form.get('caption') ? String(form.get('caption')) : '';
    const fileValue = form.get('file');
    const file = fileValue instanceof File ? fileValue : null;

    if (!file || !phoneNumber) return new HttpResponse(null, { status: 400 });

    const nowMs = Date.now();
    const nowIso = new Date(nowMs).toISOString();
    const digits = phoneDigits(phoneNumber);

    // Sul media non c'è ripiego: servirebbe un template con header media approvato.
    if (!mockFreeFormAllowed(digits, personId)) {
      return mockWindowConflict(digits, personId);
    }

    const session = attachOrCreateMockSession(digits, personId, nowMs, nowIso);

    const mime = file.type || 'application/octet-stream';
    const type: WhatsAppMessageType = mime.startsWith('image/')
      ? 'IMAGE'
      : mime.startsWith('audio/')
        ? 'AUDIO'
        : mime.startsWith('video/')
          ? 'VIDEO'
          : 'DOCUMENT';

    const message = {
      whatsAppMessageId: nextId++,
      whatsAppSessionId: session.whatsAppSessionId,
      direction: 'OUT' as const,
      externalId: `wamid.mock-${nowMs}`,
      phoneNumber: digits,
      senderName: '',
      phoneNumberId: session.phoneNumberId,
      type,
      body: caption,
      mediaMimeType: mime,
      mediaFileName: file.name,
      receiptDate: nowIso,
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: nowIso,
      modifiedDate: nowIso,
    };
    mockWhatsAppMessages.push(message);
    mockMediaBlobs.set(message.whatsAppMessageId, file);
    return HttpResponse.json(whatsAppMessageToDto(message));
  }),

  // GET contenuto media: serve il blob inviato a runtime; 404 se assente (i dati
  // seed non hanno media).
  http.get(`${API}/whatsapp-message/:id/media/content`, ({ params }) => {
    const blob = mockMediaBlobs.get(Number(params.id));
    if (!blob) return new HttpResponse(null, { status: 404 });
    return new HttpResponse(blob, {
      headers: { 'Content-Type': blob.type || 'application/octet-stream' },
    });
  }),

  // POST trascrizione della nota vocale. Riproduce il contratto del backend, rami di
  // rifiuto compresi: un mock che risponde sempre 200 nasconde esattamente i casi che
  // poi si rompono in produzione.
  //
  //   404 messaggio inesistente
  //   422 non è una nota vocale (file audio allegato, testo, template)
  //   200 già trascritto -> restituisce quanto salvato, senza "ritrascrivere"
  //   200 trascrizione nuova
  //
  // Il ritardo simula l'attesa reale: la chiamata al motore è sincrona e non è
  // istantanea, ed è il motivo per cui il tasto mostra "Trascrizione in corso...".
  http.post(`${API}/whatsapp-message/:id/transcribe`, async ({ params }) => {
    const id = Number(params.id);
    const message = mockWhatsAppMessages.find(
      (m) => m.whatsAppMessageId === id,
    );
    if (!message) return new HttpResponse(null, { status: 404 });

    if (message.type !== 'AUDIO' || !message.voiceNote) {
      return new HttpResponse(null, { status: 422 });
    }

    if (message.transcriptionDate) {
      return HttpResponse.json(whatsAppMessageToDto(message));
    }

    await delay(1500);

    message.transcription =
      'Ciao, ti mando questo vocale al volo: ho parlato con il fornitore e i tempi di consegna si sono allungati di una settimana. Fammi sapere se riusciamo comunque a rispettare la data che avevamo dato al cliente, altrimenti lo avviso io.';
    message.transcriptionSummary =
      'I tempi di consegna del fornitore slittano di una settimana. Va deciso se la data promessa al cliente regge.';
    message.transcriptionActionItems =
      'Verificare se la data promessa al cliente e\' ancora rispettabile\nAvvisare il cliente in caso contrario';
    message.transcriptionDate = new Date().toISOString();

    return HttpResponse.json(whatsAppMessageToDto(message));
  }),

  http.get(`${API}/whatsapp-sessions/:groupId/:personId`, ({ params }) => {
    const personId = Number(params.personId);

    const items = mockWhatsAppSessions
      .filter((s) => s.personId === personId)
      .map((s) => {
        const msgs = mockSessionMessages(s.whatsAppSessionId);
        const last = mockSessionLastMessageDate(s);
        const start = msgs.length ? msgs[0].receiptDate : s.createDate;
        const window = mockWhatsAppWindow(s.whatsAppSessionId);
        return {
          whatsAppSessionId: s.whatsAppSessionId,
          companyId: s.companyId,
          groupId: s.groupId,
          userId: s.userId,
          userName: s.userName,
          createDate: s.createDate,
          modifiedDate: s.modifiedDate,
          phoneNumber: s.phoneNumber,
          phoneNumberId: s.phoneNumberId,
          personId: s.personId,
          startDate: start,
          lastMessageDate: last,
          messageCount: msgs.length,
          windowState: window.windowState,
          windowExpirationDate: window.windowExpirationDate,
          messages: msgs.map(whatsAppMessageToDto),
        };
      })
      .sort((a, b) =>
        (b.lastMessageDate ?? '').localeCompare(a.lastMessageDate ?? ''),
      );

    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),

  // Conversazioni della pagina WhatsApp: per contatto, la sessione col messaggio più
  // recente; i numeri non in anagrafica (personId 0) restano fuori, come il backend.
  http.get(`${API}/whatsapp-conversations/:groupId`, () => {
    const latest = new Map<number, { phoneNumber: string; lastMessageDate: string }>();

    for (const s of mockWhatsAppSessions) {
      if (s.personId <= 0) continue;
      const last = mockSessionLastMessageDate(s);
      const current = latest.get(s.personId);
      if (!current || last > current.lastMessageDate) {
        latest.set(s.personId, { phoneNumber: s.phoneNumber, lastMessageDate: last });
      }
    }

    const items = [...latest.entries()]
      .map(([personId, c]) => {
        const person = mockPersons.find((p) => p.personId === personId);
        return person
          ? { personId, personName: `${person.firstName} ${person.lastName}`.trim(), ...c }
          : null;
      })
      .filter((c) => c !== null)
      .sort((a, b) => b.lastMessageDate.localeCompare(a.lastMessageDate));

    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),

  // DELETE sessione: rimuove la sessione e, a cascata, i suoi messaggi.
  http.delete(`${API}/whatsapp-session/:id`, ({ params }) => {
    const id = Number(params.id);
    const index = mockWhatsAppSessions.findIndex(
      (s) => s.whatsAppSessionId === id,
    );
    if (index === -1) return new HttpResponse(null, { status: 404 });
    mockWhatsAppSessions.splice(index, 1);
    for (let i = mockWhatsAppMessages.length - 1; i >= 0; i--) {
      if (mockWhatsAppMessages[i].whatsAppSessionId === id) {
        mockWhatsAppMessages.splice(i, 1);
      }
    }
    // Relazioni che puntano alla sessione (quelle che portano il pin): stesso cascade del
    // backend, o resterebbero righe verso un classPK che non esiste piu'.
    for (let i = mockRelationships.length - 1; i >= 0; i--) {
      const r = mockRelationships[i];
      if ((r.className1 === WHATSAPP_SESSION_FQN && r.classPK1 === id) ||
          (r.className2 === WHATSAPP_SESSION_FQN && r.classPK2 === id)) {
        mockRelationships.splice(i, 1);
      }
    }
    return new HttpResponse(null, { status: 204 });
  }),

  // ============================================================
  // QUOTATION — path e verbi allineati a QuotationsResourceImpl.java
  //   collection: /quotations/{groupId}    (GET list+search, POST create)
  //   single:     /quotation/{quotationId} (GET, PATCH, DELETE)
  //
  // Errori simulati, con gli stessi `type` che emettono gli exception
  // mapper del backend:
  //   - 400 MandatoryBusinessId / MandatoryTitle / InvalidStatus
  //   - 400 TitleTooLong / NotesTooLong
  //   - 409 DuplicateQuotationExternalCode
  //   - 404 quotationId inesistente
  // ============================================================
  http.get(`${API}/quotations/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const pipelineId = Number(url.searchParams.get('pipelineId') ?? 0);
    const kanbanId = Number(url.searchParams.get('kanbanId') ?? 0);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockQuotations;
    // Board della kanban (TS-101): filtro STUB = preventivi con currentStateId tra gli stati
    // della pipeline della board (senza i non assegnati). Ha precedenza sul pipelineId legacy.
    if (kanbanId > 0) {
      const { kanban, stateIds } = kanbanStateIds(kanbanId);
      // Prima gli stati della pipeline, POI il filtro: l'ordine e' quello del backend,
      // il filtro restringe dentro la pipeline e non la sostituisce.
      result = result.filter(
        (q) => stateIds.has(q.currentStateId) && matchesKanbanFilter(q, kanban?.filter),
      );
    } else if (pipelineId > 0) {
      // Picker legacy (TS-94): con pipelineId, i selezionabili sono quelli gia in quella
      // pipeline (currentStateId tra i suoi stati) o non associati (currentStateId=0).
      const pipeline = mockPipelines.find((p) => p.pipelineId === pipelineId);
      const stateIds = new Set((pipeline?.states ?? []).map((s) => s.pipelineStateId));
      result = result.filter((q) => q.currentStateId === 0 || stateIds.has(q.currentStateId));
    }
    if (search) {
      // Stesse tre colonne del backend: externalCode, title, notes. I plugin
      // abilitati estendono la ricerca ai propri campi via
      // QuotationSearchContributor, che qui non ha equivalente.
      const needle = search.toLowerCase();
      result = result.filter(
        (q) =>
          (q.externalCode ?? '').toLowerCase().includes(needle) ||
          q.title.toLowerCase().includes(needle) ||
          (q.notes ?? '').toLowerCase().includes(needle)
      );
    }
    result = applySortSpec(result, sort ?? 'modifiedDate:desc');

    return HttpResponse.json(buildPage(result, page, pageSize, quotationToDto));
  }),

  http.post(`${API}/quotations/:groupId`, async ({ params, request }) => {
    const input = (await request.json()) as QuotationInput;
    const invalid = validateQuotationInput(input, null, Number(params.groupId));
    if (invalid) return invalid;

    const now = new Date().toISOString();
    const created: Quotation = {
      quotationId: nextId++,
      externalCode: normalizeExternalCode(input.externalCode),
      businessId: Number(input.businessId),
      title: (input.title ?? '').trim(),
      dueDate: input.dueDate ?? null,
      amount: input.amount ?? 0,
      // Server-driven: il preventivo nasce sempre in bozza, lo status
      // eventualmente inviato in creazione viene ignorato.
      status: 'draft',
      notes: input.notes ?? null,
      // TS-101: stato di pipeline scelto nella cascata (validato sopra). 0 se il
      // gruppo non ha pipeline.
      currentStateId: Number(input.currentStateId ?? 0),
      companyId: 1,
      groupId: Number(params.groupId),
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
    };
    mockQuotations.push(created);
    // Contratto Liferay (OpenAPI): POST ritorna 200, non 201.
    return HttpResponse.json(quotationToDto(created), { status: 200 });
  }),

  http.get(`${API}/quotation/:id`, ({ params }) => {
    const item = mockQuotations.find((q) => q.quotationId === Number(params.id));
    return item
      ? HttpResponse.json(quotationToDto(item))
      : new HttpResponse(null, { status: 404 });
  }),

  http.patch(`${API}/quotation/:id`, async ({ params, request }) => {
    const idx = mockQuotations.findIndex((q) => q.quotationId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const input = (await request.json()) as QuotationInput;
    const invalid = validateQuotationInput(input, mockQuotations[idx], mockQuotations[idx].groupId);
    if (invalid) return invalid;

    const patch = quotationInputFromDto(input);
    // `dueDate: null` NON svuota la scadenza: il backend fonde il campo con
    // GetterUtil.getDate, che tratta il null come "non fornito". Il mock
    // riproduce il limite invece di correggerlo, altrimenti in mock mode il
    // campo si azzererebbe e in reale no.
    if (patch.dueDate === null) delete patch.dueDate;
    if (patch.externalCode !== undefined) {
      patch.externalCode = normalizeExternalCode(patch.externalCode);
    }

    const updated: Quotation = {
      ...mockQuotations[idx],
      ...patch,
      modifiedDate: new Date().toISOString(),
    };
    mockQuotations[idx] = updated;
    return HttpResponse.json(quotationToDto(updated));
  }),

  http.delete(`${API}/quotation/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockQuotations.findIndex((q) => q.quotationId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockQuotations.splice(idx, 1);
    // Cascade: il backend cancella righe e allegati del preventivo.
    for (let i = mockQuotationFiles.length - 1; i >= 0; i--) {
      if (mockQuotationFiles[i].quotationId === id) mockQuotationFiles.splice(i, 1);
    }
    return new HttpResponse(null, { status: 204 });
  }),

  // ============================================================
  // PIPELINE — path e verbi allineati a PipelinesResourceImpl.java
  //   collection: /pipelines/{groupId}   (GET list+search, POST create)
  //   single:     /pipeline/{pipelineId} (GET, PATCH, DELETE)
  //
  // Gli stati sono EMBEDDED nel payload: create/update inviano l'intero array e
  // il mock lo riconcilia (reconcileMockPipelineStates), come il backend.
  // Errori 400 con gli stessi `type` degli exception mapper del backend.
  // ============================================================
  http.get(`${API}/pipelines/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockPipelines;
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          (p.description ?? '').toLowerCase().includes(needle)
      );
    }
    result = applySortSpec(result, sort ?? 'name:asc');

    return HttpResponse.json(buildPage(result, page, pageSize, pipelineToDto));
  }),

  http.post(`${API}/pipelines/:groupId`, async ({ params, request }) => {
    const input = (await request.json()) as PipelineInput;
    const invalid = validatePipelineInput(input, null);
    if (invalid) return invalid;

    const now = new Date().toISOString();
    const groupId = Number(params.groupId);
    const pipelineId = nextId++;
    const created: Pipeline = {
      pipelineId,
      name: (input.name ?? '').trim(),
      description: input.description ?? null,
      // Non nullo: `validatePipelineInput` ha già respinto la creazione senza tipo.
      className: (input.className ?? '').trim(),
      companyId: 1,
      groupId,
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
      states: reconcileMockPipelineStates([], input.states ?? [], pipelineId, groupId, now),
    };
    mockPipelines.push(created);

    // Parità col backend (PipelineLocalServiceImpl.addPipeline): la nuova pipeline
    // porta con sé una kanban. Se non esistevano kanban nel gruppo, bootstrap
    // una-tantum: i preventivi non assegnati (currentStateId=0) vanno allo stato
    // iniziale di questa pipeline.
    const hadKanbans = mockKanbans.some((k) => k.groupId === groupId);
    mockKanbans.push({
      kanbanId: nextId++,
      name: created.name,
      className: 'it.dvel.crm.core.model.Quotation',
      pipelineId,
      companyId: 1,
      groupId,
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
    });
    if (!hadKanbans) {
      const initial = created.states.find((s) => s.kind === 'initial');
      if (initial) {
        for (const q of mockQuotations) {
          if (q.groupId === groupId && q.currentStateId === 0)
            q.currentStateId = initial.pipelineStateId;
        }
      }
    }

    // Contratto Liferay (OpenAPI): POST ritorna 200, non 201.
    return HttpResponse.json(pipelineToDto(created), { status: 200 });
  }),

  http.get(`${API}/pipeline/:id`, ({ params }) => {
    const item = mockPipelines.find((p) => p.pipelineId === Number(params.id));
    return item
      ? HttpResponse.json(pipelineToDto(item))
      : new HttpResponse(null, { status: 404 });
  }),

  http.patch(`${API}/pipeline/:id`, async ({ params, request }) => {
    const idx = mockPipelines.findIndex((p) => p.pipelineId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const input = (await request.json()) as PipelineInput;
    const invalid = validatePipelineInput(input, mockPipelines[idx]);
    if (invalid) return invalid;

    const now = new Date().toISOString();
    const current = mockPipelines[idx];
    const updated: Pipeline = {
      ...current,
      name: input.name !== undefined ? (input.name ?? '').trim() : current.name,
      description: input.description !== undefined ? input.description : current.description,
      states: reconcileMockPipelineStates(
        current.states,
        input.states ?? [],
        current.pipelineId,
        current.groupId,
        now,
      ),
      modifiedDate: now,
    };
    mockPipelines[idx] = updated;
    return HttpResponse.json(pipelineToDto(updated));
  }),

  http.delete(`${API}/pipeline/:id`, ({ params }) => {
    const id = Number(params.id);
    const idx = mockPipelines.findIndex((p) => p.pipelineId === id);
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    // Cascade come il backend (deletePipeline): azzero il currentStateId dei preventivi che erano
    // in uno degli stati di questa pipeline, rimuovo le sue board, poi la pipeline (stati embedded).
    const stateIds = new Set(mockPipelines[idx].states.map((s) => s.pipelineStateId));
    for (const q of mockQuotations) {
      if (stateIds.has(q.currentStateId)) q.currentStateId = 0;
    }
    for (let i = mockKanbans.length - 1; i >= 0; i--) {
      if (mockKanbans[i].pipelineId === id) mockKanbans.splice(i, 1);
    }

    mockPipelines.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // ============================================================
  // KANBAN — path e verbi allineati a KanbansResourceImpl.java
  //   collection: /kanbans/{groupId}  (GET list+search, POST create)
  //   single:     /kanban/{kanbanId}  (GET, PATCH rinomina, DELETE)
  // TS-101: nessuna membership persistita. La board è una VISTA a filtro: i preventivi
  // li carica il client da GET /quotations?kanbanId= (il server risolve il filtro, oggi
  // stub = stati della pipeline della board). Lo stato/colonna del preventivo è
  // currentStateId: il drag-to-move passa da PATCH /quotation/{id}, non da qui.
  // TS-102 reintrodurrà un endpoint/step per definire il filtro della board.
  // ============================================================
  http.get(`${API}/kanbans/:groupId`, ({ request }) => {
    const url = new URL(request.url);
    const search = url.searchParams.get('search');
    const sort = url.searchParams.get('sort');
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));

    let result = mockKanbans;
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter((k) => k.name.toLowerCase().includes(needle));
    }
    result = applySortSpec(result, sort ?? 'name:asc');

    return HttpResponse.json(buildPage(result, page, pageSize, kanbanToDto));
  }),

  // TS-102: catalogo dei campi filtrabili. Envelope Page come gli altri cataloghi:
  // REST Builder serializza Page.of(...) anche dove l'openapi dichiara type: array.
  // TS-117: il className e' OBBLIGATORIO e il catalogo e' suo. Tipo senza contributore =>
  // elenco vuoto, non errore: e' la stessa risposta di "questo tipo non ha campi filtrabili".
  http.get(`${API}/kanbans/filter-fields/:groupId`, ({ request }) => {
    const fields = mockFilterFields(new URL(request.url).searchParams.get('className') ?? '');
    return HttpResponse.json(buildPage(fields, 1, fields.length, (x) => x));
  }),

  http.post(`${API}/kanbans/:groupId`, async ({ params, request }) => {
    const input = (await request.json()) as KanbanInput;

    if (!(input.name ?? '').trim())
      return HttpResponse.json({ title: 'Il nome è obbligatorio', type: 'MandatoryName' }, { status: 400 });
    if (!(Number(input.pipelineId) > 0))
      return HttpResponse.json({ title: 'Selezionare una pipeline valida', type: 'MandatoryPipelineId' }, { status: 400 });

    // Stesse regole di validateFilter, stesso type dell'eccezione: il mock deve
    // rispondere come il backend anche quando rifiuta.
    // Il tipo della bacheca che nasce e' quello della pipeline scelta, ed e' il catalogo
    // contro cui il filtro va validato.
    const filterError = validateKanbanFilter(
      input.filter,
      mockPipelines.find((p) => p.pipelineId === Number(input.pipelineId))?.className ?? '');
    if (filterError)
      return HttpResponse.json(
        { title: `Filtro non valido: ${filterError}`, type: 'the-board-filter-is-not-valid' },
        { status: 400 },
      );

    const now = new Date().toISOString();
    const pipelineId = Number(input.pipelineId);

    const created: Kanban = {
      kanbanId: nextId++,
      name: (input.name ?? '').trim(),
      className: input.className ?? 'it.dvel.crm.core.model.Quotation',
      pipelineId,
      filter: input.filter?.groups.length ? input.filter : null,
      companyId: 1,
      groupId: Number(params.groupId),
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
    };

    mockKanbans.push(created);
    return HttpResponse.json(kanbanToDto(created), { status: 200 });
  }),

  http.get(`${API}/kanban/:id`, ({ params }) => {
    const kanban = mockKanbans.find((k) => k.kanbanId === Number(params.id));
    return kanban
      ? HttpResponse.json(kanbanToDto(kanban))
      : new HttpResponse(null, { status: 404 });
  }),

  http.patch(`${API}/kanban/:id`, async ({ params, request }) => {
    const idx = mockKanbans.findIndex((k) => k.kanbanId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const input = (await request.json()) as KanbanInput;

    const filterError = validateKanbanFilter(input.filter, mockKanbans[idx].className);
    if (filterError)
      return HttpResponse.json(
        { title: `Filtro non valido: ${filterError}`, type: 'the-board-filter-is-not-valid' },
        { status: 400 },
      );

    const patch = kanbanInputFromDto(input);
    const updated: Kanban = {
      ...mockKanbans[idx],
      ...patch,
      modifiedDate: new Date().toISOString(),
    };
    mockKanbans[idx] = updated;
    return HttpResponse.json(kanbanToDto(updated));
  }),

  http.delete(`${API}/kanban/:id`, ({ params }) => {
    const idx = mockKanbans.findIndex((k) => k.kanbanId === Number(params.id));
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    // TS-101: la board non possiede i preventivi (filtro a runtime): eliminarla non tocca
    // il currentStateId di alcun preventivo — restano nei loro stati di pipeline.
    mockKanbans.splice(idx, 1);

    return new HttpResponse(null, { status: 204 });
  }),

  // ============================================================
  // QUOTATION FILE — risorsa annidata, POST e PATCH in multipart.
  //   /quotation/{quotationId}/file[/{quotationFileId}]
  // I metadati tecnici (extension, mimeType, size) li determina il
  // server: qui si ricavano dal File caricato, come fa il backend.
  // ============================================================
  http.get(`${API}/quotation/:quotationId/file`, ({ params }) => {
    const items = mockQuotationFiles.filter((f) => f.quotationId === Number(params.quotationId));
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), quotationFileToDto));
  }),

  http.post(`${API}/quotation/:quotationId/file`, async ({ params, request }) => {
    const quotationId = Number(params.quotationId);
    if (!mockQuotations.some((q) => q.quotationId === quotationId)) {
      return new HttpResponse(null, { status: 404 });
    }
    const form = await request.formData();
    const file = form.get('file');
    if (!(file instanceof File)) {
      return HttpResponse.json(
        { title: 'Il contenuto del file e\' obbligatorio', type: 'QuotationFileException' },
        { status: 400 }
      );
    }
    const fileName = String(form.get('fileName') ?? file.name);
    const now = new Date().toISOString();
    const created = withMockPreview({
      quotationFileId: nextId++,
      quotationId,
      fileEntryId: nextId++,
      fileName,
      extension: fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '',
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
      type: quotationFileTypeFromDto(Number(form.get('type') ?? 200)),
      companyId: 1,
      groupId: MOCK_GROUP_ID,
      userId: 1,
      userName: 'Sistema',
      createDate: now,
      modifiedDate: now,
    });
    mockQuotationFiles.push(created);
    return HttpResponse.json(quotationFileToDto(created), { status: 200 });
  }),

  http.get(`${API}/quotation/:quotationId/file/:id`, ({ params }) => {
    const item = mockQuotationFiles.find(
      (f) =>
        f.quotationFileId === Number(params.id) &&
        f.quotationId === Number(params.quotationId)
    );
    return item
      ? HttpResponse.json(quotationFileToDto(item))
      : new HttpResponse(null, { status: 404 });
  }),

  http.patch(`${API}/quotation/:quotationId/file/:id`, async ({ params, request }) => {
    const idx = mockQuotationFiles.findIndex(
      (f) =>
        f.quotationFileId === Number(params.id) &&
        f.quotationId === Number(params.quotationId)
    );
    if (idx === -1) return new HttpResponse(null, { status: 404 });

    const form = await request.formData();
    const current = mockQuotationFiles[idx];
    const file = form.get('file');
    const replacing = file instanceof File;
    const fileName = String(form.get('fileName') ?? (replacing ? file.name : current.fileName));
    const updated: QuotationFile = withMockPreview({
      ...current,
      fileName,
      extension: fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '',
      mimeType: replacing ? file.type || current.mimeType : current.mimeType,
      size: replacing ? file.size : current.size,
      type: quotationFileTypeFromDto(Number(form.get('type') ?? 200)),
      modifiedDate: new Date().toISOString(),
    });
    mockQuotationFiles[idx] = updated;
    return HttpResponse.json(quotationFileToDto(updated));
  }),

  http.delete(`${API}/quotation/:quotationId/file/:id`, ({ params }) => {
    const idx = mockQuotationFiles.findIndex(
      (f) =>
        f.quotationFileId === Number(params.id) &&
        f.quotationId === Number(params.quotationId)
    );
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockQuotationFiles.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  // ============================================================
  // QUOTATION ITEM — risorsa annidata sotto il preventivo.
  //   /quotation/{quotationId}/item[/{quotationItemId}]
  // Il totale del preventivo è la somma delle righe e lo ricalcola il
  // servizio core a ogni scrittura: qui si riproduce, altrimenti aggiungere
  // una riga in mock lascerebbe il totale fermo e sembrerebbe un difetto.
  // ============================================================
  http.get(`${API}/quotation/:quotationId/item`, ({ params }) => {
    const items = mockQuotationItems
      .filter((i) => i.quotationId === Number(params.quotationId))
      .sort((a, b) => a.position - b.position);
    return HttpResponse.json(buildPage(items, 1, Math.max(items.length, 1), (x) => x));
  }),

  http.post(`${API}/quotation/:quotationId/item`, async ({ params, request }) => {
    const quotationId = Number(params.quotationId);
    if (!mockQuotations.some((q) => q.quotationId === quotationId))
      return new HttpResponse(null, { status: 404 });

    const body = (await request.json()) as Partial<QuotationItem>;
    const now = new Date().toISOString();
    const created: QuotationItem = {
      quotationItemId: nextId++,
      quotationId,
      // position mancante o zero: si accoda. È il comportamento dichiarato
      // di addQuotationItem, non una comodita' del mock.
      position: body.position && body.position > 0 ? body.position : nextItemPosition(quotationId),
      quantity: body.quantity ?? 0,
      amount: body.amount ?? 0,
      notes: body.notes ?? '',
      companyId: 1, groupId: MOCK_GROUP_ID, userId: 1, userName: mockLoginUser.fullName,
      createDate: now, modifiedDate: now,
    };
    mockQuotationItems.push(created);
    recalculateQuotationAmount(quotationId);
    return HttpResponse.json(created, { status: 200 });
  }),

  http.get(`${API}/quotation/:quotationId/item/:id`, ({ params }) => {
    const item = mockQuotationItems.find(
      (i) => i.quotationItemId === Number(params.id) &&
        i.quotationId === Number(params.quotationId),
    );
    if (!item) return new HttpResponse(null, { status: 404 });
    return HttpResponse.json(item);
  }),

  http.patch(`${API}/quotation/:quotationId/item/:id`, async ({ params, request }) => {
    const item = mockQuotationItems.find(
      (i) => i.quotationItemId === Number(params.id) &&
        i.quotationId === Number(params.quotationId),
    );
    if (!item) return new HttpResponse(null, { status: 404 });

    const patch = (await request.json()) as Partial<QuotationItem>;
    if (patch.position !== undefined) item.position = patch.position;
    if (patch.quantity !== undefined) item.quantity = patch.quantity;
    if (patch.amount !== undefined) item.amount = patch.amount;
    if (patch.notes !== undefined) item.notes = patch.notes ?? '';
    item.modifiedDate = new Date().toISOString();

    recalculateQuotationAmount(item.quotationId);
    return HttpResponse.json(item);
  }),

  http.delete(`${API}/quotation/:quotationId/item/:id`, ({ params }) => {
    const idx = mockQuotationItems.findIndex(
      (i) => i.quotationItemId === Number(params.id) &&
        i.quotationId === Number(params.quotationId),
    );
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    const quotationId = mockQuotationItems[idx].quotationId;
    mockQuotationItems.splice(idx, 1);
    recalculateQuotationAmount(quotationId);
    return new HttpResponse(null, { status: 204 });
  }),

  // Riordino: rinumera da 1 a N in un colpo solo. Il 400 quando la lista non
  // nomina tutte le righe una volta ciascuna non è pignoleria del mock — è il
  // comportamento del servizio vero, e il client ci conta per accorgersi di
  // stare lavorando su un elenco vecchio.
  http.put(`${API}/quotation/:quotationId/item-order`, async ({ params, request }) => {
    const quotationId = Number(params.quotationId);

    if (!mockQuotations.some((q) => q.quotationId === quotationId))
      return new HttpResponse(null, { status: 404 });

    const rows = mockQuotationItems.filter((i) => i.quotationId === quotationId);
    const { quotationItemIds } = (await request.json()) as { quotationItemIds?: number[] };

    if (!quotationItemIds || quotationItemIds.length !== rows.length)
      return new HttpResponse(null, { status: 400 });

    // La mappa si consuma: una riga nominata due volte non c'è più al secondo
    // giro, e il solo conteggio non basterebbe a scoprirlo.
    const byId = new Map(rows.map((i) => [i.quotationItemId, i]));
    const now = new Date().toISOString();
    const reordered: QuotationItem[] = [];

    for (const [index, quotationItemId] of quotationItemIds.entries()) {
      const item = byId.get(quotationItemId);
      if (!item) return new HttpResponse(null, { status: 400 });

      byId.delete(quotationItemId);
      item.position = index + 1;
      item.modifiedDate = now;
      reordered.push(item);
    }

    // Nessun ricalcolo dell'importo: l'ordine non cambia la somma.
    return HttpResponse.json(buildPage(reordered, 1, Math.max(reordered.length, 1), (x) => x));
  }),

  // ============================================================
  // TEAM — allineato a teamApi.ts (com.liferay.portal.kernel.model.Team)
  //   GET    /teams/{groupId}                  -> Page<TeamDto> (lista con membri)
  //   POST   /teams/{groupId}                  -> TeamDto (crea)
  //   PATCH  /team/{teamId}                     -> TeamDto (nome/descrizione)
  //   DELETE /team/{teamId}                     -> 204
  //   POST   /team/{teamId}/member/{userId}     -> TeamDto (aggiunge membro)
  //   DELETE /team/{teamId}/member/{userId}     -> TeamDto (rimuove membro)
  // I membri sono risolti da memberIds contro MOCK_USERS. Segment-count diversi
  // evitano collisioni MSW tra /teams/:groupId, /team/:teamId e le sub-resource.
  // ============================================================
  http.get(`${API}/teams/:groupId`, ({ params }) => {
    const groupId = Number(params.groupId);
    const all = mockTeams.filter((t) => t.groupId === groupId);
    return HttpResponse.json(buildPage(all, 1, Math.max(all.length, 1), toTeamDto));
  }),
  http.post(`${API}/teams/:groupId`, async ({ params, request }) => {
    const body = (await request.json()) as { name?: string; description?: string };
    const name = (body.name ?? '').trim();
    if (!name) {
      return HttpResponse.json(
        { title: 'Il nome del team e\' obbligatorio', type: 'MandatoryName' },
        { status: 400 },
      );
    }
    const created: MockTeam = {
      teamId: consumeNextTeamId(),
      groupId: Number(params.groupId),
      name,
      description: body.description ?? '',
      memberIds: [],
    };
    mockTeams.push(created);
    return HttpResponse.json(toTeamDto(created));
  }),
  http.patch(`${API}/team/:teamId`, async ({ params, request }) => {
    const team = mockTeams.find((t) => t.teamId === Number(params.teamId));
    if (!team) return new HttpResponse(null, { status: 404 });
    const body = (await request.json()) as { name?: string; description?: string };
    if (body.name !== undefined) team.name = body.name;
    if (body.description !== undefined) team.description = body.description;
    return HttpResponse.json(toTeamDto(team));
  }),
  http.delete(`${API}/team/:teamId`, ({ params }) => {
    const idx = mockTeams.findIndex((t) => t.teamId === Number(params.teamId));
    if (idx === -1) return new HttpResponse(null, { status: 404 });
    mockTeams.splice(idx, 1);
    return new HttpResponse(null, { status: 204 });
  }),
  http.post(`${API}/team/:teamId/member/:userId`, ({ params }) => {
    const team = mockTeams.find((t) => t.teamId === Number(params.teamId));
    if (!team) return new HttpResponse(null, { status: 404 });
    const userId = Number(params.userId);
    if (!team.memberIds.includes(userId)) team.memberIds.push(userId);
    return HttpResponse.json(toTeamDto(team));
  }),
  http.delete(`${API}/team/:teamId/member/:userId`, ({ params }) => {
    const team = mockTeams.find((t) => t.teamId === Number(params.teamId));
    if (!team) return new HttpResponse(null, { status: 404 });
    const userId = Number(params.userId);
    team.memberIds = team.memberIds.filter((id) => id !== userId);
    return HttpResponse.json(toTeamDto(team));
  }),

  // ============================================================
  // USER — gestione stato e impostazioni utente (userApi.ts / authSlice.ts).
  //   PATCH /user/{userId}/status?status=  -> MemberDto (attiva/disattiva)
  //   PUT   /user/{userId}/password        -> 200 (password impostata dall'amministratore)
  //   GET   /user/settings                 -> UserSettings
  //   PATCH /user/settings                 -> UserSettings (echo del body)
  //   PATCH /user/profile                  -> User (profilo aggiornato)
  //   POST  /user/updatePassword           -> 200 (nessun corpo)
  // /user/{userId}/status ha 3 segmenti: non collide con /user/settings|profile
  // (2 segmenti) ne' con gli handler auth /user/login|refresh|init|logout.
  // ============================================================
  http.patch(`${API}/user/:userId/status`, ({ params, request }) => {
    const u = MOCK_USERS.find((x) => x.userId === Number(params.userId));
    if (!u) return new HttpResponse(null, { status: 404 });
    const status = Number(new URL(request.url).searchParams.get('status') ?? 0);
    return HttpResponse.json({ ...u, status });
  }),
  // FILO-78 — Password impostata dall'amministratore: copie diverse 400, come Liferay.
  http.put(`${API}/user/:userId/password`, async ({ params, request }) => {
    if (!MOCK_USERS.some((x) => x.userId === Number(params.userId))) {
      return new HttpResponse(null, { status: 404 });
    }
    const body = (await request.json()) as { password1?: string; password2?: string };
    if (!body.password1 || body.password1 !== body.password2) {
      return HttpResponse.json(
        { status: 'BAD_REQUEST', title: 'Le password inserite non coincidono.' }, { status: 400 });
    }
    return new HttpResponse(null, { status: 200 });
  }),
  // TS-139 — Codice gestionale: vuoto lo toglie (il backend lo omette), un codice già di un altro
  // utente risponde 409 come Liferay (ERC unico nella company).
  http.patch(`${API}/user/:userId/externalReferenceCode`, ({ params, request }) => {
    const u = MOCK_USERS.find((x) => x.userId === Number(params.userId)) as
      (typeof MOCK_USERS)[number] & { externalReferenceCode?: string };
    if (!u) return new HttpResponse(null, { status: 404 });
    const code = (new URL(request.url).searchParams.get('externalReferenceCode') ?? '').trim();
    const taken = MOCK_USERS.some((x) => x.userId !== u.userId &&
      (x as { externalReferenceCode?: string }).externalReferenceCode === code);
    if (code && taken) {
      return HttpResponse.json(
        { status: 'CONFLICT', title: 'Codice già assegnato a un altro utente' }, { status: 409 });
    }
    if (code) u.externalReferenceCode = code;
    else delete u.externalReferenceCode;
    return HttpResponse.json(u);
  }),
  http.get(`${API}/user/settings`, () =>
    HttpResponse.json({ taskDeadline: '7', taskTime: '09:00', taskReminder: '60' }),
  ),
  http.patch(`${API}/user/settings`, async ({ request }) => {
    const body = (await request.json()) as {
      taskDeadline?: string;
      taskTime?: string;
      taskReminder?: string;
    };
    return HttpResponse.json({
      taskDeadline: body.taskDeadline ?? '7',
      taskTime: body.taskTime ?? '09:00',
      taskReminder: body.taskReminder ?? '60',
    });
  }),
  http.patch(`${API}/user/profile`, async ({ request }) => {
    const body = (await request.json()) as {
      firstName?: string;
      lastName?: string;
      emailAddress?: string;
      languageId?: string;
      profileFile?: string;
    };
    const base = MOCK_LOGIN_RESPONSE.user;
    const firstName = body.firstName ?? base.firstName;
    const lastName = body.lastName ?? base.lastName;
    // Ritratto: il mock non ha un repository immagini, quindi rimanda indietro
    // lo stesso file come data URL — `resolvePortraitURL` li lascia passare
    // così come sono. È quel che fa già PATCH /company/logo: senza, il campo
    // restava vuoto e l'avatar dell'header continuava a mostrare le iniziali
    // anche dopo il salvataggio. Il crop produce JPEG (AvatarCropDialog).
    const portraitURL = body.profileFile
      ? `data:image/jpeg;base64,${body.profileFile}`
      : base.portraitURL;
    const updated: User = {
      ...base,
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      emailAddress: body.emailAddress ?? base.emailAddress,
      languageId: body.languageId ?? base.languageId,
      portraitURL,
    };
    // Scritto anche sull'utente del mock, così /user/init lo ritorna a chi
    // rilegge il profilo nella stessa sessione (es. dopo un refresh del token).
    // Come ogni altro dato di questi handler vive in memoria: al reload della
    // pagina il modulo si ricarica e il ritratto torna quello di partenza.
    MOCK_LOGIN_RESPONSE.user.portraitURL = portraitURL;
    return HttpResponse.json(updated);
  }),
  http.post(`${API}/user/updatePassword`, () => new HttpResponse(null, { status: 200 })),

  // GET /user/auditLog — storico accessi paginato (read-only, user-scoped).
  http.get(`${API}/user/auditLog`, ({ request }) => {
    const url = new URL(request.url);
    const page = Math.max(1, Number(url.searchParams.get('page') ?? 1));
    const pageSize = Math.max(1, Number(url.searchParams.get('pageSize') ?? 20));
    return HttpResponse.json(buildPage(mockAuditLog, page, pageSize, (e) => e));
  }),

  // POST /automation/{id}/new-version — clona la definition in una nuova versione
  // DRAFT (id nuovo, version+1). Allineato a automationApi.createVersion.
  http.post(`${API}/automation/:id/new-version`, ({ params }) => {
    const a = mockAutomations.find((x) => x.automationDefinitionId === Number(params.id));
    if (!a) return new HttpResponse(null, { status: 404 });
    const now = new Date().toISOString();
    const clone: Automation = {
      ...a,
      automationDefinitionId: nextId++,
      version: a.version + 1,
      status: 'draft',
      createDate: now,
      modifiedDate: now,
    };

    // I due rami si RICREANO con id propri invece di restare quelli dell'originale, che lo spread
    // qui sopra copierebbe per riferimento: il server, clonando, crea righe nuove sotto la copia, e
    // due versioni che condividono l'identità delle azioni non esistono da nessuna parte.
    applyMockAutomationConfiguration(clone, {
      conditionGroups: a.conditionGroups,
      actions: a.actions,
    });

    mockAutomations.push(clone);
    return HttpResponse.json(automationToDto(clone));
  }),

  // ============================================================
  // RELATIONSHIP EXTRA — proiezioni contributor + pin lookup (relationshipApi.ts)
  //   GET /relationship/{id}/has-pinned                         -> boolean
  //   GET /relationships/{groupId}/from-contributors            -> RelationshipsFromContributors
  //   GET /relationships/{groupId}/preview-from-contributors    -> RelationshipsFromContributors
  //   GET /relationships/{groupId}/contributor-records          -> Page<ContributorRelationship>
  // Derivati dai mockRelationships esistenti; i titoli via resolveEntityTitle.
  // ============================================================
  http.get(`${API}/relationship/:id/has-pinned`, ({ params }) => {
    const rel = mockRelationships.find((r) => r.relationshipId === Number(params.id));
    if (!rel) return HttpResponse.json(false);
    const has = mockRelationships.some(
      (r) => r.className1 === rel.className1 && r.classPK1 === rel.classPK1 && r.pinned,
    );
    return HttpResponse.json(has);
  }),
  http.get(`${API}/relationships/:groupId/from-contributors`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className');
    const classPK = p.get('classPK');
    if (!className || !classPK) {
      return HttpResponse.json({ title: 'className e classPK obbligatori' }, { status: 400 });
    }
    return HttpResponse.json(
      buildFromContributors(className, Number(classPK), (relationshipId) => relationshipId),
    );
  }),
  http.get(`${API}/relationships/:groupId/preview-from-contributors`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className');
    const referenceClassName = p.get('referenceClassName');
    const referenceClassPK = p.get('referenceClassPK');
    if (!className || !referenceClassName || !referenceClassPK) {
      return HttpResponse.json(
        { title: 'className, referenceClassName e referenceClassPK obbligatori' },
        { status: 400 },
      );
    }
    // La preview parte dalle associazioni dell'entità di CONTESTO (reference) e le
    // propone come pre-associazioni: relationshipId 0, la relazione non esiste ancora.
    return HttpResponse.json(
      buildFromContributors(referenceClassName, Number(referenceClassPK), () => 0),
    );
  }),
  http.get(`${API}/relationships/:groupId/contributor-records`, ({ request }) => {
    const p = new URL(request.url).searchParams;
    const className = p.get('className') ?? '';
    const search = (p.get('search') ?? '').toLowerCase();
    const page = Math.max(1, Number(p.get('page') ?? 1));
    const pageSize = Math.max(1, Number(p.get('pageSize') ?? 20));

    let records: { relationshipId: number; classPK: number; title: string; description: string | null }[] = [];
    if (className.endsWith('.Person')) {
      records = mockPersons.map((x) => ({
        relationshipId: 0,
        classPK: x.personId,
        title: `${x.firstName} ${x.lastName}`.trim(),
        description: null,
      }));
    } else if (className.endsWith('.Business')) {
      records = mockBusinesses.map((x) => ({
        relationshipId: 0,
        classPK: x.businessId,
        title: x.name,
        description: null,
      }));
    } else if (className.endsWith('.Quotation')) {
      records = mockQuotations.map((x) => ({
        relationshipId: 0,
        classPK: x.quotationId,
        title: x.title,
        description: null,
      }));
    }
    if (search) records = records.filter((r) => r.title.toLowerCase().includes(search));
    return HttpResponse.json(buildPage(records, page, pageSize, (r) => r));
  }),
];

/** Prossima posizione libera nel preventivo. Le posizioni partono da 1. */
function nextItemPosition(quotationId: number): number {
  const positions = mockQuotationItems
    .filter((i) => i.quotationId === quotationId)
    .map((i) => i.position);
  return positions.length === 0 ? 1 : Math.max(...positions) + 1;
}

/**
 * Riproduce `recalculateQuotationAmount` del servizio core: il totale del
 * preventivo è la somma di quantita' per importo delle sue righe.
 *
 * Senza, il mock direbbe una bugia comoda - le righe cambiano e il totale
 * resta - e il primo salvataggio contro il backend vero sembrerebbe un difetto
 * di regressione invece del comportamento corretto.
 */
function recalculateQuotationAmount(quotationId: number): void {
  const quotation = mockQuotations.find((q) => q.quotationId === quotationId);
  if (!quotation) return;
  quotation.amount = mockQuotationItems
    .filter((i) => i.quotationId === quotationId)
    .reduce((total, i) => total + i.quantity * i.amount, 0);
}

